/**
 * FinalBattleSystem — Manages the complete endgame sequence when
 * the player has collected all 4 Agmuth relics and completes a level.
 *
 * Phases:
 *   0. Trigger (detected in GameScene, hands off to this system)
 *   1. Celestial Exit Materialization (~2s)
 *   2. Brother Malachar Modal (gift: 1 Heart Potion)
 *   3. Transition on entering celestial exit
 *   4. Threia Modal (unique celestial golden style)
 *   5. Vorath Arena Materialization
 *   6. Vorath Modal (threatening message)
 *   7. Battle — Vorath launches dark spheres
 *   8. Altar Activation (player walks to 4 corner altars)
 *   9. Vorath Destruction (beams + explosion)
 *  10. Final Threia Modal (game ending)
 *  11. THE END screen
 */

import {
  TEX_CELESTIAL_EXIT,
  TEX_VORATH_MASS,
  TEX_ALTAR_THREIA,
  TEX_DARK_SPHERE,
  TEX_BEAM_PARTICLE,
  TEX_GLOW,
  TEX_SMOKE,
  TEX_PLAYER,
  TEX_WALL_STONE,
  CellType,
  Events,
  SCENE_TITLE,
  SCENE_HUD,
} from '../utils/constants.js';

import {
  TILE_SIZE,
  GAME_WIDTH,
  GAME_HEIGHT,
  FINAL_CELESTIAL_EXIT_MATERIALIZE_DURATION,
  FINAL_VORATH_MAZE_COLS,
  FINAL_VORATH_MAZE_ROWS,
  FINAL_VORATH_PRISON_SIZE,
  FINAL_VORATH_SPHERE_DAMAGE,
  FINAL_VORATH_SPHERE_INITIAL_INTERVAL,
  FINAL_VORATH_SPHERE_MIN_INTERVAL,
  FINAL_VORATH_SPHERE_ACCEL,
  FINAL_VORATH_SPHERE_SPEED,
  FINAL_VORATH_SPHERE_SIZE,
  FINAL_VORATH_PULSE_SPEED,
  FINAL_ALTAR_BEAM_INTERVAL,
  FINAL_ALTAR_BEAM_DURATION,
  FINAL_VORATH_DESTROY_DURATION,
  FINAL_VORATH_SIZE,
  FINAL_ALTAR_SIZE,
  FINAL_PLAYER_PROXIMITY_TRIGGER,
  FINAL_VORATH_CONTACT_DAMAGE,
  FINAL_VORATH_CONTACT_RADIUS,
  FINAL_VORATH_REPEL_DURATION,
  FINAL_VORATH_INVULN_DURATION,
  FINAL_VORATH_HOMING_SPHERE_SPEED,
  FINAL_VORATH_HOMING_GRACE_MS,
  COLOR_CELESTIAL_EXIT,
  COLOR_CELESTIAL_GOLD,
  COLOR_CELESTIAL_ORANGE,
  COLOR_VORATH_DARK,
  COLOR_VORATH_RED,
  COLOR_VORATH_ORANGE,
  COLOR_ALTAR_GOLD,
  COLOR_ALTAR_CELESTIAL,
  COLOR_DARK_SPHERE,
  COLOR_DARK_SPHERE_RIM,
  COLOR_THREIA_MODAL_BG,
  COLOR_THREIA_MODAL_BORDER,
  COLOR_THREIA_MODAL_TEXT,
  COLOR_THREIA_MODAL_NAME,
  COLOR_NARRATOR_VORATH,
  COLOR_NARRATOR_VORATH_NAME,
  COLOR_NARRATOR_VORATH_BG,
  COLOR_NARRATOR_MONK,
  COLOR_NARRATOR_MONK_NAME,
  AUDIO_CELESTIAL_MATERIALIZE,
  AUDIO_CELESTIAL_HUM,
  AUDIO_FINAL_ENERGY,
  AUDIO_THREIA_VOICE,
  AUDIO_THREIA_MUSIC,
  AUDIO_VORATH_MACABRE,
  AUDIO_VORATH_ARENA_MUSIC,
  AUDIO_VORATH_SPHERE_LAUNCH,
  AUDIO_VORATH_SPHERE_HIT,
  AUDIO_VORATH_SPHERE_WALL,
  AUDIO_ALTAR_ACTIVATE,
  AUDIO_ALTAR_BEAM,
  AUDIO_VORATH_DESTROY,
  AUDIO_FINAL_CELESTIAL,
  AUDIO_FINAL_MUSIC,
  AUDIO_FINAL_TRANSITION,
  AUDIO_MENU_CLICK,
  AUDIO_NARRATOR_MONK,
  SHOP_ITEM_HEART_POTION,
  HUD_HEIGHT,
  BORDER_MARGIN,
  COLOR_WHITE,
  COLOR_BLACK,
} from '../config/game-config.js';

import MazeGenerator from './MazeGenerator.js';
import { getSfxVol, getMusicVol } from '../utils/audio-config.js';
import { getOwnedItems, setOwnedItems } from './ShopManager.js';
import { deleteSave } from '../utils/save-manager.js';

/**
 * @typedef {object} FinalBattleConfig
 * @property {Phaser.Scene} scene - The GameScene instance
 * @property {Phaser.Events.EventEmitter} eventBus - Shared event bus
 * @property {object} healthSystem - HealthSystem reference
 * @property {object} player - Player entity
 */

export default class FinalBattleSystem {
  /**
   * @param {FinalBattleConfig} config
   */
  constructor(config) {
    /** @type {Phaser.Scene} */
    this.scene = config.scene;
    /** @type {Phaser.Events.EventEmitter} */
    this.eventBus = config.eventBus;
    /** @type {object} */
    this.healthSystem = config.healthSystem;

    /** @type {boolean} */
    this._active = false;
    /** @type {string} */
    this._phase = 'idle'; // idle | materializing | malachar | walking_to_exit | transition | threia | arena_build | vorath_modal | battle | altars_done | destruction | final_threia | the_end

    // Celestial exit state
    this._celestialExitSprite = null;
    this._celestialExitCol = 0;
    this._celestialExitRow = 0;

    // Arena state
    this._arenaMaze = null;
    this._arenaGrid = null;
    this._arenaWallGroup = null;
    this._arenaFogGraphics = null;
    this._vorathSprite = null;
    this._vorathPrisonCenter = null; // { col, row } in grid coords
    this._altars = []; // Array of { sprite, col, row, activated }
    this._altarsActivated = 0;
    this._vorathCoreBounds = null; // { minGc, maxGc, minGr, maxGr } in expanded grid
    this._protectedWallCells = null; // Set of 'r,c' keys — walls that spheres must not destroy

    // Battle state
    this._sphereGroup = null;
    this._sphereTimer = null;
    this._currentSphereInterval = FINAL_VORATH_SPHERE_INITIAL_INTERVAL;
    this._playerInvulnerable = false;

    // Beam tracking (for cleanup on explosion)
    this._beamElements = [];
    this._vorathVibrateTween = null;

    // Music references
    this._arenaMusic = null;
    this._vorathAmbient = null;
    this._threiaMusic = null;
    this._finalMusic = null;
    this._beamSounds = [];

    // Cleanup tracking
    this._allElements = []; // all game objects to destroy on cleanup
    this._allTweens = []; // tween references
    this._allTimers = []; // timer references
  }

  /** @returns {boolean} Whether the final battle is currently active */
  get isActive() {
    return this._active;
  }

  /**
   * Whether the player is inside the Vorath battle arena (transition phase
   * or later).  During the earlier phases (materializing, malachar,
   * walking_to_exit) the player is still in the normal maze and normal
   * gameplay rules (health drain, hazards, etc.) must apply.
   * @returns {boolean}
   */
  get isInArena() {
    if (!this._active) return false;
    // Phases before the arena: player is still in the normal maze
    const preArenaPhases = ['materializing', 'malachar', 'walking_to_exit'];
    return !preArenaPhases.includes(this._phase);
  }

  /** @returns {string} Current phase */
  get phase() {
    return this._phase;
  }

  // ===============================================================
  // Phase 1: Celestial Exit Materialization
  // ===============================================================

  /**
   * Start the final battle sequence.
   * Called by GameScene after _buildNextLevel() when agmuthCount >= 4.
   * The new level has been built normally — we just add the celestial exit.
   */
  start() {
    this._active = true;
    this._phase = 'materializing';
    this.eventBus.emit(Events.FINAL_BATTLE_START);

    // Lock player input
    this.scene._transitionLock = true;

    // Use the player's actual current position (gridCol/gridRow) rather than
    // playerStartCol/Row, which may be stale from the previous level.
    const startCol = this.scene.player
      ? this.scene.player.gridCol
      : this.scene.playerStartCol;
    const startRow = this.scene.player
      ? this.scene.player.gridRow
      : this.scene.playerStartRow;

    // Try cells adjacent to start — prefer south, then east, west, north.
    // In the expanded grid, adjacent logical cells are ±2 away. The cell
    // at ±1 is the corridor/wall between them — it must be PATH (open
    // corridor) so the player can walk to the exit without phasing.
    const directions = [
      { dc: 0, dr: 2 },   // south
      { dc: 2, dr: 0 },   // east
      { dc: -2, dr: 0 },  // west
      { dc: 0, dr: -2 },  // north
    ];

    let exitCol = -1;
    let exitRow = -1;

    const grid = this.scene.mazeGrid;

    // First pass: adjacent cell with open corridor between player and exit
    for (const dir of directions) {
      const tc = startCol + dir.dc;
      const tr = startRow + dir.dr;
      if (!grid[tr] || grid[tr][tc] !== CellType.PATH) continue;
      // Corridor cell between player and target must be open
      const wc = startCol + dir.dc / 2;
      const wr = startRow + dir.dr / 2;
      if (!grid[wr] || grid[wr][wc] !== CellType.PATH) continue;
      exitCol = tc;
      exitRow = tr;
      break;
    }

    // Fallback: if no adjacent cell with open corridor found, search for
    // the nearest PATH cell reachable from the player start position.
    if (exitCol < 0) {
      const gridRows = grid.length;
      const gridCols = grid[0].length;
      let bestDist = Infinity;

      for (let r = 1; r < gridRows - 1; r++) {
        for (let c = 1; c < gridCols - 1; c++) {
          if (grid[r][c] !== CellType.PATH) continue;
          if (r === startRow && c === startCol) continue; // skip player cell
          const d = Math.abs(r - startRow) + Math.abs(c - startCol);
          if (d < bestDist) {
            bestDist = d;
            exitCol = c;
            exitRow = r;
          }
        }
      }
    }

    this._celestialExitCol = exitCol;
    this._celestialExitRow = exitRow;

    // Convert grid coords to pixel coords
    const px = exitCol * TILE_SIZE + TILE_SIZE / 2;
    const py = exitRow * TILE_SIZE + TILE_SIZE / 2;

    // ------------------------------------------------------------------
    // Phase 1: Dramatic materialization sequence (~2 seconds)
    // The tile flashes with light, the portal fades in with flickering,
    // golden particles swirl, and once solid the exit pulses with white
    // light and a subtle reality-distortion halo.
    // ------------------------------------------------------------------

    // 1a. Tile light pulses — a circle on the floor that flashes
    //     rhythmically during the 2-second materialization.
    const tileGlow = this.scene.add.circle(px, py, TILE_SIZE * 0.7, 0xffffff, 0);
    tileGlow.setDepth(3);
    this._allElements.push(tileGlow);

    const tileGlowTween = this.scene.tweens.add({
      targets: tileGlow,
      alpha: { from: 0, to: 0.7 },
      duration: 250,
      yoyo: true,
      repeat: 7,          // 8 pulses × 500ms = 4s, but we stop early
      ease: 'Sine.easeInOut',
    });
    this._allTweens.push(tileGlowTween);

    // Broader light flash that reaches further out
    const wideFlash = this.scene.add.circle(px, py, TILE_SIZE * 2.5, 0xffffff, 0);
    wideFlash.setDepth(3);
    this._allElements.push(wideFlash);

    const wideFlashTween = this.scene.tweens.add({
      targets: wideFlash,
      alpha: { from: 0, to: 0.35 },
      duration: 400,
      yoyo: true,
      repeat: 4,
      ease: 'Sine.easeInOut',
    });
    this._allTweens.push(wideFlashTween);

    // Play materialization sound
    try { this.scene.sound.play(AUDIO_CELESTIAL_MATERIALIZE, { volume: getSfxVol(0.6) }); } catch (_) {}

    // 1b. Golden/white particle swirl during materialization
    const matParticles = this.scene.add.particles(px, py, TEX_GLOW, {
      lifespan: { min: 300, max: 700 },
      speed: { min: 15, max: 50 },
      scale: { start: 0.5, end: 0 },
      alpha: { start: 0.9, end: 0 },
      tint: [COLOR_CELESTIAL_GOLD, 0xffffff, COLOR_CELESTIAL_ORANGE],
      frequency: 40,
      quantity: 4,
      angle: { min: 0, max: 360 },
      emitting: true,
    });
    matParticles.setDepth(7);
    this._allElements.push(matParticles);

    // 1c. Create the celestial exit sprite — completely invisible at start
    this._celestialExitSprite = this.scene.add.sprite(px, py, TEX_CELESTIAL_EXIT);
    this._celestialExitSprite.setDepth(5);
    this._celestialExitSprite.setAlpha(0);
    this._celestialExitSprite.setScale(0.1);
    this._allElements.push(this._celestialExitSprite);

    // Slow fade-in of the sprite with flickering — two overlapping tweens:
    // i) Scale grows from 0.1 → 1.0 over 2s with elastic easing
    const scaleTween = this.scene.tweens.add({
      targets: this._celestialExitSprite,
      scaleX: 1,
      scaleY: 1,
      duration: FINAL_CELESTIAL_EXIT_MATERIALIZE_DURATION,
      ease: 'Cubic.easeOut',
    });
    this._allTweens.push(scaleTween);

    // ii) Alpha flickers from 0 up to 1 with an additive chain:
    //     quick flickers for the first half, then stabilises to full.
    const flickerTimeline = this.scene.tweens.chain({
      targets: this._celestialExitSprite,
      tweens: [
        { alpha: 0.3, duration: 150, ease: 'Sine.easeOut' },
        { alpha: 0.05, duration: 100, ease: 'Sine.easeIn' },
        { alpha: 0.5, duration: 200, ease: 'Sine.easeOut' },
        { alpha: 0.1, duration: 120, ease: 'Sine.easeIn' },
        { alpha: 0.6, duration: 180, ease: 'Sine.easeOut' },
        { alpha: 0.2, duration: 100, ease: 'Sine.easeIn' },
        { alpha: 0.75, duration: 250, ease: 'Sine.easeOut' },
        { alpha: 0.4, duration: 130, ease: 'Sine.easeIn' },
        { alpha: 0.9, duration: 200, ease: 'Sine.easeOut' },
        { alpha: 0.7, duration: 100, ease: 'Sine.easeIn' },
        { alpha: 1.0, duration: 370, ease: 'Quad.easeOut' },
      ],
    });
    this._allTweens.push(flickerTimeline);

    // 1d. After materialization completes (~2.2s), transition to idle state
    const matTimer = this.scene.time.delayedCall(
      FINAL_CELESTIAL_EXIT_MATERIALIZE_DURATION + 200,
      () => {
        // Stop materialization particles
        if (matParticles && matParticles.stop) matParticles.stop();
        // Fade out tile glow
        if (tileGlow && tileGlow.active) {
          this.scene.tweens.add({
            targets: tileGlow,
            alpha: 0,
            duration: 400,
            onComplete: () => { if (tileGlow.destroy) tileGlow.destroy(); },
          });
        }
        if (wideFlash && wideFlash.active) {
          this.scene.tweens.add({
            targets: wideFlash,
            alpha: 0,
            duration: 400,
            onComplete: () => { if (wideFlash.destroy) wideFlash.destroy(); },
          });
        }

        // Ensure sprite is fully solid
        if (this._celestialExitSprite) {
          this._celestialExitSprite.setAlpha(1);
          this._celestialExitSprite.setScale(1);
        }

        // Start the idle pulse + distortion halo
        this._startCelestialPulse();

        // Start celestial hum sound (looping)
        try {
          this._celestialHum = this.scene.sound.add(AUDIO_CELESTIAL_HUM, {
            volume: getSfxVol(0.2),
            loop: true,
          });
          this._celestialHum.play();
        } catch (_) {}

        // Phase 2: Brother Malachar modal after a short pause (500ms)
        // so the player can appreciate the fully materialized portal.
        const malacharTimer = this.scene.time.delayedCall(500, () => {
          this._phase = 'malachar';
          this._showMalacharModal();
        });
        this._allTimers.push(malacharTimer);
      },
    );
    this._allTimers.push(matTimer);
  }

  /**
   * Start the idle pulsing glow and reality-distortion halo on the
   * celestial exit. The sprite gently scales up/down and a translucent
   * white halo around it pulsates to simulate a reality warp.
   */
  _startCelestialPulse() {
    if (!this._celestialExitSprite) return;

    const px = this._celestialExitSprite.x;
    const py = this._celestialExitSprite.y;

    // Sprite pulsation — gentle scale + alpha breathing
    const pulse = this.scene.tweens.add({
      targets: this._celestialExitSprite,
      scaleX: 1.12,
      scaleY: 1.12,
      alpha: { from: 0.88, to: 1.0 },
      duration: FINAL_VORATH_PULSE_SPEED,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this._allTweens.push(pulse);

    // Reality-distortion halo — a larger translucent ring that pulsates
    // around the exit, creating the impression of warped space.
    const halo = this.scene.add.circle(px, py, TILE_SIZE * 1.3, 0xffffff, 0.12);
    halo.setDepth(4);
    this._allElements.push(halo);
    this._celestialHaloSprite = halo;

    const haloPulse = this.scene.tweens.add({
      targets: halo,
      scaleX: { from: 0.9, to: 1.35 },
      scaleY: { from: 0.9, to: 1.35 },
      alpha: { from: 0.18, to: 0.04 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this._allTweens.push(haloPulse);

    // Subtle ambient golden particles drifting around the portal
    const ambientParticles = this.scene.add.particles(px, py, TEX_GLOW, {
      lifespan: { min: 600, max: 1200 },
      speed: { min: 5, max: 20 },
      scale: { start: 0.25, end: 0 },
      alpha: { start: 0.5, end: 0 },
      tint: [COLOR_CELESTIAL_GOLD, 0xffffff],
      frequency: 200,
      quantity: 1,
      angle: { min: 0, max: 360 },
      emitting: true,
    });
    ambientParticles.setDepth(6);
    this._allElements.push(ambientParticles);
  }

  // ===============================================================
  // Phase 2: Brother Malachar Modal
  // ===============================================================

  _showMalacharModal() {
    const cam = this.scene.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const elements = [];

    // Dark overlay
    const overlay = this.scene.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0x000000, 0.80,
    );
    overlay.setDepth(300).setAlpha(0);
    elements.push(overlay);

    // Modal dimensions
    const modalW = 400;
    const modalH = 310;
    const modalX = centerX - modalW / 2;
    const modalY = centerY - modalH / 2;

    // Warm double border (monk style)
    const border = this.scene.add.graphics().setDepth(301).setAlpha(0);
    border.lineStyle(2, 0xffd700, 0.45);
    border.strokeRect(modalX, modalY, modalW, modalH);
    border.lineStyle(1, 0xfff8dc, 0.2);
    border.strokeRect(modalX + 4, modalY + 4, modalW - 8, modalH - 8);
    elements.push(border);

    // Speaker name
    const nameColor = `#${COLOR_NARRATOR_MONK_NAME.toString(16).padStart(6, '0')}`;
    const nameText = this.scene.add.text(centerX, modalY + 24, 'Brother Malachar', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      fontStyle: 'bold',
      color: nameColor,
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(nameText);

    // Decorative line
    const nameLine = this.scene.add.graphics().setDepth(301).setAlpha(0);
    nameLine.lineStyle(1, 0xffd700, 0.3);
    nameLine.lineBetween(modalX + 30, modalY + 42, modalX + modalW - 30, modalY + 42);
    elements.push(nameLine);

    // Epic message
    const textColor = `#${COLOR_NARRATOR_MONK.toString(16).padStart(6, '0')}`;
    const message =
      'The four relics pulse with ancient power...\n' +
      'I can feel it — the labyrinth trembles.\n\n' +
      'Child, you have done what the Order\n' +
      'believed impossible. The path to Vorath\n' +
      'will reveal itself. Trust in Threia.\n\n' +
      'Take this potion. You will need every\n' +
      'ounce of strength for what lies ahead.';
    const bodyText = this.scene.add.text(centerX, modalY + 56, message, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: textColor,
      lineSpacing: 5,
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(301).setAlpha(0);
    elements.push(bodyText);

    // Gift notice
    const giftText = this.scene.add.text(centerX, modalY + modalH - 56,
      '[ Received: Heart Potion ]', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '11px',
        color: '#ffd700',
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(giftText);

    // Footer
    const footer = this.scene.add.text(
      centerX, modalY + modalH - 26,
      'Press ENTER or SPACE to continue', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#888888',
      },
    ).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(footer);

    // Pulsing footer
    this.scene.tweens.add({
      targets: footer,
      alpha: { from: 0.3, to: 0.8 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 400,
    });

    // Play monk sound
    try { this.scene.sound.play(AUDIO_NARRATOR_MONK, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Fade in
    this.scene.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 350,
      ease: 'Quad.easeOut',
    });
    for (const el of elements) {
      if (el === overlay) continue;
      this.scene.tweens.add({
        targets: el,
        alpha: 1,
        duration: 350,
        delay: 150,
        ease: 'Quad.easeOut',
      });
    }

    // Dismiss handler
    const dismiss = () => {
      this.scene.input.keyboard.off('keydown-ENTER', dismiss);
      this.scene.input.keyboard.off('keydown-SPACE', dismiss);

      try { this.scene.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

      // Deliver Heart Potion gift
      this._deliverHeartPotionGift();

      // Fade out elements
      for (const el of elements) {
        this.scene.tweens.add({
          targets: el,
          alpha: 0,
          duration: 250,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el && el.destroy) el.destroy();
          },
        });
      }

      // Unlock player movement — they must walk to the celestial exit
      this.scene.time.delayedCall(300, () => {
        this._phase = 'walking_to_exit';
        this.scene._transitionLock = false;
        // Start listening for player reaching the celestial exit
        this._startExitOverlapCheck();
      });
    };

    // Delay key binding
    this.scene.time.delayedCall(600, () => {
      this.scene.input.keyboard.on('keydown-ENTER', dismiss);
      this.scene.input.keyboard.on('keydown-SPACE', dismiss);
    });
  }

  /** Add a Heart Potion to the player's shop inventory */
  _deliverHeartPotionGift() {
    const items = getOwnedItems();
    items[SHOP_ITEM_HEART_POTION] = (items[SHOP_ITEM_HEART_POTION] || 0) + 1;
    setOwnedItems(items);
    this.eventBus.emit(Events.HEART_POTION_CHANGED,
      items[SHOP_ITEM_HEART_POTION]);
    this.eventBus.emit(Events.SHOP_ITEMS_CHANGED);
    this.eventBus.emit(Events.NARRATOR_GIFT_RECEIVED, 'heart_potion');
  }

  // ===============================================================
  // Exit overlap detection (Phase 2→3 transition)
  // ===============================================================

  _startExitOverlapCheck() {
    // Check frequently if player is on the celestial exit
    this._exitCheckEvent = this.scene.time.addEvent({
      delay: 50,
      loop: true,
      callback: () => {
        if (this._phase !== 'walking_to_exit') return;
        if (!this.scene.player) return;

        // Player gridCol/gridRow are grid coordinates (same space as _celestialExitCol/Row)
        const playerCol = this.scene.player.gridCol;
        const playerRow = this.scene.player.gridRow;

        if (playerCol === this._celestialExitCol && playerRow === this._celestialExitRow) {
          // Immediately lock player so they cannot walk past the exit
          this.scene._transitionLock = true;

          this._exitCheckEvent.remove();
          this._exitCheckEvent = null;
          this._playCelestialExitEffect();
        }
      },
    });
    this._allTimers.push(this._exitCheckEvent);
  }

  // ===============================================================
  // Celestial exit disappearance effect (before transition)
  // ===============================================================

  /**
   * Play a celestial disappearance animation when the player steps
   * on the exit: golden particle burst, exit shrinks/fades, golden
   * camera flash, then proceed to the transition phase.
   */
  _playCelestialExitEffect() {
    const playerX = this.scene.player.x;
    const playerY = this.scene.player.y;

    // --- Golden particle burst from the exit position ---
    const burstEmitter = this.scene.add.particles(playerX, playerY, TEX_GLOW, {
      lifespan: { min: 500, max: 1100 },
      speed: { min: 30, max: 110 },
      angle: { min: 200, max: 340 },  // Upward-biased celebratory spread
      scale: { start: 0.8, end: 0 },
      alpha: { start: 1.0, end: 0 },
      tint: [COLOR_CELESTIAL_GOLD, 0xffffff, COLOR_CELESTIAL_ORANGE],
      frequency: 30,
      quantity: 5,
      emitting: true,
    });
    burstEmitter.setDepth(7);
    this._allElements.push(burstEmitter);

    // --- Shrink + fade the celestial exit sprite ---
    if (this._celestialExitSprite) {
      // Stop any pulsing tweens on the sprite first
      this.scene.tweens.killTweensOf(this._celestialExitSprite);
      this.scene.tweens.add({
        targets: this._celestialExitSprite,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        duration: 800,
        ease: 'Back.easeIn',
        onComplete: () => {
          if (this._celestialExitSprite) {
            this._celestialExitSprite.destroy();
            this._celestialExitSprite = null;
          }
        },
      });
    }

    // --- Fade out the halo ---
    if (this._celestialHaloSprite) {
      this.scene.tweens.killTweensOf(this._celestialHaloSprite);
      this.scene.tweens.add({
        targets: this._celestialHaloSprite,
        alpha: 0,
        scaleX: 2.0,
        scaleY: 2.0,
        duration: 600,
        ease: 'Quad.easeOut',
        onComplete: () => {
          if (this._celestialHaloSprite) {
            this._celestialHaloSprite.destroy();
            this._celestialHaloSprite = null;
          }
        },
      });
    }

    // --- Shrink + fade the player sphere (must vanish before flash at 900ms) ---
    if (this.scene.player) {
      this.scene.tweens.killTweensOf(this.scene.player);
      this.scene.tweens.add({
        targets: this.scene.player,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        duration: 700,
        ease: 'Back.easeIn',
      });
    }

    // --- Intensify particles, then golden flash + transition ---
    const intensifyTimer = this.scene.time.delayedCall(500, () => {
      if (burstEmitter && burstEmitter.active) {
        burstEmitter.frequency = 15;
        burstEmitter.quantity = 10;
      }
    });
    this._allTimers.push(intensifyTimer);

    // Golden-white camera flash after 900ms, then stop particles and transition
    const flashTimer = this.scene.time.delayedCall(900, () => {
      if (!this.scene.scene) return;
      // Stop burst particles
      if (burstEmitter && burstEmitter.active) burstEmitter.stop();

      // Golden-tinted camera flash
      this.scene.cameras.main.flash(500, 255, 215, 0);

      // After flash peaks, destroy particles and begin the real transition
      const cleanupTimer = this.scene.time.delayedCall(300, () => {
        if (burstEmitter && burstEmitter.active) burstEmitter.destroy();
        this._startTransition();
      });
      this._allTimers.push(cleanupTimer);
    });
    this._allTimers.push(flashTimer);
  }

  // ===============================================================
  // Phase 3: Transition — blinding light then darkness
  // ===============================================================

  _startTransition() {
    this._phase = 'transition';
    this.scene._transitionLock = true;

    // Stop celestial hum
    if (this._celestialHum) {
      this._celestialHum.stop();
      this._celestialHum.destroy();
      this._celestialHum = null;
    }

    // Stop game music
    this.scene._stopGameMusic();

    // ── Destroy Phase 1/2 elements (celestial exit sprite, halo, particles) ──
    if (this._celestialExitSprite) {
      this._celestialExitSprite.destroy();
      this._celestialExitSprite = null;
    }
    if (this._celestialHaloSprite) {
      this._celestialHaloSprite.destroy();
      this._celestialHaloSprite = null;
    }
    // Destroy any ambient particle emitters from Phase 1
    this._allElements = this._allElements.filter(el => {
      if (el && el.type === 'ParticleEmitter' && el.active) {
        el.stop();
        el.destroy();
        return false;
      }
      return true;
    });

    // Portal entry sound
    try { this.scene.sound.play(AUDIO_FINAL_TRANSITION, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Viewport-fixed black overlay for the fade
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const blackOverlay = this.scene.add.rectangle(cx, cy, GAME_WIDTH * 2, GAME_HEIGHT * 2, 0x000000, 0);
    blackOverlay.setDepth(500).setScrollFactor(0);
    this._allElements.push(blackOverlay);

    // Simple fade to black over 1.5 seconds
    this.scene.tweens.add({
      targets: blackOverlay,
      alpha: 1,
      duration: 1500,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Everything is black — cleanup the old level
        this._cleanupOldLevel();

        // Short pause in darkness, then show Threia modal
        const threiaTimer = this.scene.time.delayedCall(800, () => {
          this._phase = 'threia';
          this._showThreiaModal(blackOverlay);
        });
        this._allTimers.push(threiaTimer);
      },
    });
  }

  /** Clean up the old maze level before building the arena */
  _cleanupOldLevel() {
    // Use GameScene's cleanup but keep the HUD running
    this.scene._cleanup();
  }

  // ===============================================================
  // Phase 4: Threia Modal
  // ===============================================================

  _showThreiaModal(blackOverlay) {
    const cam = this.scene.cameras.main;
    // Reset camera to origin for the modal since we cleaned up
    cam.scrollX = 0;
    cam.scrollY = 0;

    // Use viewport center (scrollFactor 0) for perfect centering
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2;
    const elements = [];

    // Reposition blackOverlay to cover viewport (scrollFactor 0)
    if (blackOverlay) {
      blackOverlay.setPosition(centerX, centerY);
      blackOverlay.setScrollFactor(0);
      blackOverlay.setDepth(500);
    }

    // Celestial sound
    try { this.scene.sound.play(AUDIO_THREIA_VOICE, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Threia background music
    try {
      this._threiaMusic = this.scene.sound.add(AUDIO_THREIA_MUSIC, {
        volume: getMusicVol(0.3),
        loop: true,
      });
      this._threiaMusic.play();
    } catch (_) {}

    // Modal dimensions — slightly larger for Threia
    const modalW = 420;
    const modalH = 320;
    const modalX = centerX - modalW / 2;
    const modalY = centerY - modalH / 2;

    // Deep blue-black background (unique to Threia)
    const bg = this.scene.add.rectangle(
      centerX, centerY,
      modalW, modalH,
      COLOR_THREIA_MODAL_BG, 0.95,
    );
    bg.setDepth(501).setAlpha(0).setScrollFactor(0);
    elements.push(bg);

    // Triple golden border with glow effect
    const border = this.scene.add.graphics().setDepth(502).setAlpha(0).setScrollFactor(0);
    border.lineStyle(3, COLOR_THREIA_MODAL_BORDER, 0.9);
    border.strokeRect(modalX, modalY, modalW, modalH);
    border.lineStyle(1.5, COLOR_THREIA_MODAL_BORDER, 0.4);
    border.strokeRect(modalX + 5, modalY + 5, modalW - 10, modalH - 10);
    border.lineStyle(1, COLOR_THREIA_MODAL_BORDER, 0.15);
    border.strokeRect(modalX + 9, modalY + 9, modalW - 18, modalH - 18);
    elements.push(border);

    // Glowing border effect — pulsing golden circles at corners
    for (let i = 0; i < 4; i++) {
      const cx = i < 2 ? modalX + 3 : modalX + modalW - 3;
      const cy = i % 2 === 0 ? modalY + 3 : modalY + modalH - 3;
      const cornerGlow = this.scene.add.circle(cx, cy, 4, COLOR_THREIA_MODAL_BORDER, 0.5);
      cornerGlow.setDepth(503).setAlpha(0).setScrollFactor(0);
      elements.push(cornerGlow);
      this.scene.tweens.add({
        targets: cornerGlow,
        alpha: { from: 0.2, to: 0.7 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: i * 300,
      });
    }

    // Speaker name
    const nameColor = `#${COLOR_THREIA_MODAL_NAME.toString(16).padStart(6, '0')}`;
    const nameText = this.scene.add.text(centerX, modalY + 26, 'Threia', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '18px',
      fontStyle: 'bold',
      color: nameColor,
    }).setOrigin(0.5).setDepth(503).setAlpha(0).setScrollFactor(0);
    elements.push(nameText);

    // Decorative golden line
    const nameLine = this.scene.add.graphics().setDepth(503).setAlpha(0).setScrollFactor(0);
    nameLine.lineStyle(1, COLOR_THREIA_MODAL_BORDER, 0.5);
    nameLine.lineBetween(modalX + 40, modalY + 46, modalX + modalW - 40, modalY + 46);
    elements.push(nameLine);

    // Threia's message
    const textColor = `#${COLOR_THREIA_MODAL_TEXT.toString(16).padStart(6, '0')}`;
    const message =
      'Brave soul... Vorath awaits beyond this\n' +
      'threshold, consumed by his endless greed\n' +
      'for energy and lost souls.\n\n' +
      'Be careful. Have courage.\n\n' +
      'I cannot intervene directly — it is my\n' +
      'greatest suffering. But know that I\n' +
      'yearn with all my being for your triumph.\n\n' +
      'Go now. End this ancient torment.';
    const bodyText = this.scene.add.text(centerX, modalY + 58, message, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: textColor,
      lineSpacing: 5,
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(503).setAlpha(0).setScrollFactor(0);
    elements.push(bodyText);

    // Footer
    const footer = this.scene.add.text(
      centerX, modalY + modalH - 26,
      'Press ENTER or SPACE to continue', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#8888aa',
      },
    ).setOrigin(0.5).setDepth(503).setAlpha(0).setScrollFactor(0);
    elements.push(footer);
    this.scene.tweens.add({
      targets: footer,
      alpha: { from: 0.3, to: 0.8 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 400,
    });

    // Fade in
    for (const el of elements) {
      this.scene.tweens.add({
        targets: el,
        alpha: el === bg ? 0.95 : 1,
        duration: 500,
        delay: 200,
        ease: 'Quad.easeOut',
      });
    }

    // Dismiss
    const dismiss = () => {
      this.scene.input.keyboard.off('keydown-ENTER', dismiss);
      this.scene.input.keyboard.off('keydown-SPACE', dismiss);

      try { this.scene.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

      // Stop Threia music
      if (this._threiaMusic) {
        this._threiaMusic.stop();
        this._threiaMusic.destroy();
        this._threiaMusic = null;
      }

      // Fade out modal elements (NOT the blackOverlay — keep it for arena transition)
      for (const el of elements) {
        this.scene.tweens.add({
          targets: el,
          alpha: 0,
          duration: 300,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el && el.destroy) el.destroy();
          },
        });
      }

      // Proceed to Phase 5: Arena materialization
      this.scene.time.delayedCall(500, () => {
        this._phase = 'arena_build';
        this._buildVorathArena(blackOverlay);
      });
    };

    this.scene.time.delayedCall(800, () => {
      this.scene.input.keyboard.on('keydown-ENTER', dismiss);
      this.scene.input.keyboard.on('keydown-SPACE', dismiss);
    });
  }

  // ===============================================================
  // Phase 5: Vorath Arena Materialization
  // ===============================================================

  _buildVorathArena(blackOverlay) {
    // Generate a level-1 sized maze for the arena
    const cols = FINAL_VORATH_MAZE_COLS;
    const rows = FINAL_VORATH_MAZE_ROWS;
    const gen = new MazeGenerator(cols, rows);
    const result = gen.generate();
    this._arenaGrid = result.grid;
    this._arenaMazeCols = cols;
    this._arenaMazeRows = rows;

    // Calculate grid dimensions
    const gridCols = 2 * cols + 1;
    const gridRows = 2 * rows + 1;

    // Carve the 5x5 prison in the center
    const prisonSize = FINAL_VORATH_PRISON_SIZE;
    const centerLogCol = Math.floor(cols / 2);
    const centerLogRow = Math.floor(rows / 2);
    this._vorathPrisonCenter = { col: centerLogCol, row: centerLogRow };

    // Carve the interior of the prison PLUS a buffer zone around it.
    // The prison is 5×5 logical cells (halfPrison=2). Vorath is 192px
    // (8 grid cells diameter), so we add 1 extra logical-cell ring as a
    // buffer so Vorath never overlaps or touches maze walls.
    const halfPrison = Math.floor(prisonSize / 2);
    const bufferRing = 1; // extra logical-cell ring cleared around the prison
    const halfCleared = halfPrison + bufferRing; // total cleared radius (3 logical cells)

    for (let dr = -halfCleared; dr <= halfCleared; dr++) {
      for (let dc = -halfCleared; dc <= halfCleared; dc++) {
        const logC = centerLogCol + dc;
        const logR = centerLogRow + dr;
        if (logC < 0 || logC >= cols || logR < 0 || logR >= rows) continue;

        // Make the logical cell walkable
        const gc = logC * 2 + 1;
        const gr = logR * 2 + 1;
        this._arenaGrid[gr][gc] = CellType.PATH;

        // Make corridor cells between adjacent cleared cells walkable
        if (dc < halfCleared) {
          const rightWall = gc + 1;
          if (rightWall < gridCols) this._arenaGrid[gr][rightWall] = CellType.PATH;
        }
        if (dr < halfCleared) {
          const belowWall = gr + 1;
          if (belowWall < gridRows) this._arenaGrid[belowWall][gc] = CellType.PATH;
        }
        // Also clear diagonal corridor intersections
        if (dc < halfCleared && dr < halfCleared) {
          const diagC = gc + 1;
          const diagR = gr + 1;
          if (diagC < gridCols && diagR < gridRows) {
            this._arenaGrid[diagR][diagC] = CellType.PATH;
          }
        }
      }
    }

    // Clear the grid-level wall ring surrounding the entire cleared area
    // so no stray wall cells remain adjacent to the buffer zone.
    for (let dr = -halfCleared; dr <= halfCleared; dr++) {
      for (let dc = -halfCleared; dc <= halfCleared; dc++) {
        if (Math.abs(dr) !== halfCleared && Math.abs(dc) !== halfCleared) continue;
        const logC = centerLogCol + dc;
        const logR = centerLogRow + dr;
        if (logC < 0 || logC >= cols || logR < 0 || logR >= rows) continue;
        const gc = logC * 2 + 1;
        const gr = logR * 2 + 1;

        // Clear walls on the outer edges of the buffer zone
        if (dr === -halfCleared && gr > 0) this._arenaGrid[gr - 1][gc] = CellType.PATH;
        if (dr === halfCleared && gr + 1 < gridRows) this._arenaGrid[gr + 1][gc] = CellType.PATH;
        if (dc === -halfCleared && gc > 0) this._arenaGrid[gr][gc - 1] = CellType.PATH;
        if (dc === halfCleared && gc + 1 < gridCols) this._arenaGrid[gr][gc + 1] = CellType.PATH;
      }
    }

    // Re-mark Vorath's core cells as WALL so the player cannot walk
    // into the Vorath mass. The core is 3×3 logical cells (5×5 expanded
    // grid cells) centered on the prison center. The surrounding buffer
    // zone remains PATH so the player can walk around Vorath.
    const coreHalf = 1; // 3×3 logical core: from -1 to +1
    const centerGc = centerLogCol * 2 + 1;
    const centerGr = centerLogRow * 2 + 1;

    // Store core bounds for sphere collision checks (spheres should not
    // destroy these wall cells — they are Vorath's body)
    this._vorathCoreBounds = {
      minGc: centerGc - coreHalf * 2 - 1,
      maxGc: centerGc + coreHalf * 2 + 1,
      minGr: centerGr - coreHalf * 2 - 1,
      maxGr: centerGr + coreHalf * 2 + 1,
    };

    for (let dr = -coreHalf; dr <= coreHalf; dr++) {
      for (let dc = -coreHalf; dc <= coreHalf; dc++) {
        // Mark the logical cell
        const lgc = centerGc + dc * 2;
        const lgr = centerGr + dr * 2;
        if (lgr >= 0 && lgr < gridRows && lgc >= 0 && lgc < gridCols) {
          this._arenaGrid[lgr][lgc] = CellType.WALL;
        }
        // Mark the corridor cells between adjacent core cells
        if (dc < coreHalf) {
          const corrC = lgc + 1;
          if (lgr >= 0 && lgr < gridRows && corrC >= 0 && corrC < gridCols) {
            this._arenaGrid[lgr][corrC] = CellType.WALL;
          }
        }
        if (dr < coreHalf) {
          const corrR = lgr + 1;
          if (corrR >= 0 && corrR < gridRows && lgc >= 0 && lgc < gridCols) {
            this._arenaGrid[corrR][lgc] = CellType.WALL;
          }
        }
        // Mark diagonal corridor intersections
        if (dc < coreHalf && dr < coreHalf) {
          const diagC = lgc + 1;
          const diagR = lgr + 1;
          if (diagR >= 0 && diagR < gridRows && diagC >= 0 && diagC < gridCols) {
            this._arenaGrid[diagR][diagC] = CellType.WALL;
          }
        }
      }
    }

    // Ensure paths from the cleared zone to the maze corridors exist
    // Carve paths from all 4 edges of the buffer zone outward
    const openings = [
      { dc: 0, dr: -halfCleared - 1 }, // north
      { dc: 0, dr: halfCleared + 1 },  // south
      { dc: -halfCleared - 1, dr: 0 }, // west
      { dc: halfCleared + 1, dr: 0 },  // east
    ];
    for (const op of openings) {
      const logC = centerLogCol + op.dc;
      const logR = centerLogRow + op.dr;
      if (logC >= 0 && logC < cols && logR >= 0 && logR < rows) {
        const gc = logC * 2 + 1;
        const gr = logR * 2 + 1;
        this._arenaGrid[gr][gc] = CellType.PATH;
        // Also open the wall between this cell and the cleared zone edge
        const wallC = gc - (op.dc > 0 ? 1 : (op.dc < 0 ? -1 : 0));
        const wallR = gr - (op.dr > 0 ? 1 : (op.dr < 0 ? -1 : 0));
        if (wallR >= 0 && wallR < gridRows && wallC >= 0 && wallC < gridCols) {
          this._arenaGrid[wallR][wallC] = CellType.PATH;
        }
      }
    }

    // Place player near the prison (just outside the buffer, north side)
    const playerLogCol = centerLogCol;
    const playerLogRow = centerLogRow - halfCleared - 1;
    const playerGridCol = playerLogCol * 2 + 1;
    const playerGridRow = playerLogRow * 2 + 1;
    // Ensure this cell is walkable
    if (playerGridRow >= 0 && playerGridRow < gridRows) {
      this._arenaGrid[playerGridRow][playerGridCol] = CellType.PATH;
    }

    // Store player start for the arena (grid coordinates, matching MazeGenerator convention)
    this._arenaPlayerCol = playerGridCol;
    this._arenaPlayerRow = playerGridRow;

    // ── Pre-carve altar zones BEFORE wall rendering ──
    // The altars are 48px (2×2 tiles). We carve a 7×7 expanded-grid area
    // around each altar center so no wall sprites are rendered there.
    // We also build a protected-wall set: the ring of walls just outside
    // the carved zone must NOT be destructible by dark spheres — this
    // keeps the altar areas structurally intact.
    const altarCorners = [
      { col: 1, row: 1 },
      { col: this._arenaMazeCols - 2, row: 1 },
      { col: 1, row: this._arenaMazeRows - 2 },
      { col: this._arenaMazeCols - 2, row: this._arenaMazeRows - 2 },
    ];

    this._protectedWallCells = new Set();

    for (const corner of altarCorners) {
      const gc = corner.col * 2 + 1;
      const gr = corner.row * 2 + 1;

      // Clear a 7×7 expanded grid area centered on the altar
      for (let dr = -3; dr <= 3; dr++) {
        for (let dc = -3; dc <= 3; dc++) {
          const nc = gc + dc;
          const nr = gr + dr;
          if (nr >= 1 && nr < gridRows - 1 && nc >= 1 && nc < gridCols - 1) {
            this._arenaGrid[nr][nc] = CellType.PATH;
          }
        }
      }

      // Build the protected wall ring: cells at distance 4 from altar
      // center that are still WALL. These form the structural boundary
      // around each altar and must be indestructible.
      for (let dr = -4; dr <= 4; dr++) {
        for (let dc = -4; dc <= 4; dc++) {
          // Only the outermost ring (distance 4 in Chebyshev / infinity norm)
          if (Math.abs(dr) < 4 && Math.abs(dc) < 4) continue;
          const nc = gc + dc;
          const nr = gr + dr;
          if (nr >= 0 && nr < gridRows && nc >= 0 && nc < gridCols) {
            if (this._arenaGrid[nr][nc] === CellType.WALL) {
              this._protectedWallCells.add(`${nr},${nc}`);
            }
          }
        }
      }
    }

    // Now build the visual arena
    this._renderArena(blackOverlay);
  }

  _renderArena(blackOverlay) {
    const gridCols = 2 * this._arenaMazeCols + 1;
    const gridRows = 2 * this._arenaMazeRows + 1;

    // Calculate maze pixel dimensions and offset to center it
    const mazePixelW = gridCols * TILE_SIZE;
    const mazePixelH = gridRows * TILE_SIZE;

    // Build wall sprites
    this._arenaWallGroup = this.scene.add.group();

    // Use stone texture with reduced brightness for Vorath's arena
    const wallTex = TEX_WALL_STONE;
    const wallTint = 0x999999; // darken walls for a gloomier arena feel

    for (let r = 0; r < gridRows; r++) {
      for (let c = 0; c < gridCols; c++) {
        if (this._arenaGrid[r][c] === CellType.WALL) {
          // Skip rendering wall sprites inside Vorath's core — the
          // Vorath sprite covers this area visually.
          const cb = this._vorathCoreBounds;
          if (cb && c >= cb.minGc && c <= cb.maxGc && r >= cb.minGr && r <= cb.maxGr) {
            continue;
          }

          const x = c * TILE_SIZE + TILE_SIZE / 2;
          const y = r * TILE_SIZE + TILE_SIZE / 2;
          const wallSprite = this.scene.add.sprite(x, y, wallTex);
          wallSprite.setTint(wallTint);
          wallSprite.setDepth(2);
          wallSprite.setAlpha(0); // start invisible
          this._arenaWallGroup.add(wallSprite);
          this._allElements.push(wallSprite);
        }
      }
    }

    // Create exit marker placeholder (no actual exit in arena)

    // Create player at the arena start position (already grid coords)
    const playerPx = this._arenaPlayerCol * TILE_SIZE + TILE_SIZE / 2;
    const playerPy = this._arenaPlayerRow * TILE_SIZE + TILE_SIZE / 2;

    // Import Player class dynamically isn't great — GameScene should create the player.
    // Instead, we'll have GameScene create the player for us.
    // Store the position and let the integration code handle it.
    this._arenaPlayerPx = playerPx;
    this._arenaPlayerPy = playerPy;

    // Create Vorath sprite in the center
    const vorathPx = (this._vorathPrisonCenter.col * 2 + 1) * TILE_SIZE + TILE_SIZE / 2;
    const vorathPy = (this._vorathPrisonCenter.row * 2 + 1) * TILE_SIZE + TILE_SIZE / 2;

    this._vorathSprite = this.scene.add.sprite(vorathPx, vorathPy, TEX_VORATH_MASS);
    this._vorathSprite.setDepth(10);
    this._vorathSprite.setAlpha(0);
    // No downscale — display at full 96px size to fill the prison area
    this._allElements.push(this._vorathSprite);

    // Store Vorath pixel position
    this._vorathPx = vorathPx;
    this._vorathPy = vorathPy;

    // Create 4 altars at the corners.
    // Grid carving for altar zones was already done in _buildVorathArena()
    // before wall sprites were rendered — no walls overlap the altars.
    this._altars = [];
    const corners = [
      { col: 1, row: 1 },
      { col: this._arenaMazeCols - 2, row: 1 },
      { col: 1, row: this._arenaMazeRows - 2 },
      { col: this._arenaMazeCols - 2, row: this._arenaMazeRows - 2 },
    ];

    for (const corner of corners) {
      const ax = (corner.col * 2 + 1) * TILE_SIZE + TILE_SIZE / 2;
      const ay = (corner.row * 2 + 1) * TILE_SIZE + TILE_SIZE / 2;

      const altarSprite = this.scene.add.sprite(ax, ay, TEX_ALTAR_THREIA);
      altarSprite.setDepth(5);
      altarSprite.setAlpha(0);
      this._allElements.push(altarSprite);

      this._altars.push({
        sprite: altarSprite,
        col: corner.col,
        row: corner.row,
        px: ax,
        py: ay,
        activated: false,
      });
    }

    // Initialize visited array (all revealed — no fog in arena)
    this._arenaVisited = [];
    for (let r = 0; r < gridRows; r++) {
      this._arenaVisited[r] = [];
      for (let c = 0; c < gridCols; c++) {
        this._arenaVisited[r][c] = 1;
      }
    }

    // Materialize the arena
    this._materializeArena(blackOverlay);
  }

  _materializeArena(blackOverlay) {
    const cam = this.scene.cameras.main;

    // Center camera on the arena maze
    const gridCols = 2 * this._arenaMazeCols + 1;
    const gridRows = 2 * this._arenaMazeRows + 1;
    const mazePixelW = gridCols * TILE_SIZE;
    const mazePixelH = gridRows * TILE_SIZE;

    // Set world bounds
    this.scene.physics.world.setBounds(0, 0, mazePixelW, mazePixelH);

    // Ensure blackOverlay covers the full viewport
    if (blackOverlay) {
      blackOverlay.setPosition(GAME_WIDTH / 2, GAME_HEIGHT / 2);
      blackOverlay.setScrollFactor(0);
      blackOverlay.setDepth(500);
      blackOverlay.setAlpha(1);
    }

    // Arena materialization sound
    try { this.scene.sound.play(AUDIO_FINAL_ENERGY, { volume: getSfxVol(0.45) }); } catch (_) {}

    // ── Fade out black overlay over 2s while maze materializes underneath ──

    // Walls materialize with staggered fade-in
    const walls = this._arenaWallGroup.getChildren();
    const mazeCenterX = mazePixelW / 2;
    const mazeCenterY = mazePixelH / 2;

    // Sort walls by distance from center (center first, edges last)
    const wallsWithDist = walls.map(w => ({
      sprite: w,
      dist: Math.sqrt((w.x - mazeCenterX) ** 2 + (w.y - mazeCenterY) ** 2),
    }));
    wallsWithDist.sort((a, b) => a.dist - b.dist);
    const maxDist = wallsWithDist.length > 0
      ? wallsWithDist[wallsWithDist.length - 1].dist
      : 1;

    for (let i = 0; i < wallsWithDist.length; i++) {
      const { sprite, dist } = wallsWithDist[i];
      // Delay based on distance from center (0ms to 1600ms)
      const delay = (dist / maxDist) * 1600;
      sprite.setScale(0.3);
      this.scene.tweens.add({
        targets: sprite,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 400 + Math.random() * 200,
        delay,
        ease: 'Back.easeOut',
      });
    }

    // Vorath fades in after walls begin appearing
    this.scene.tweens.add({
      targets: this._vorathSprite,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 1200,
      delay: 800,
      ease: 'Back.easeOut',
    });

    // Fade out the black overlay to reveal everything
    if (blackOverlay) {
      this.scene.tweens.add({
        targets: blackOverlay,
        alpha: 0,
        duration: 2000,
        ease: 'Quad.easeOut',
        onComplete: () => {
          if (blackOverlay && blackOverlay.destroy) blackOverlay.destroy();
        },
      });
    }

    // Start Vorath pulsing animation after fully materialized
    const vorathPulse = this.scene.time.delayedCall(2200, () => {
      if (!this._vorathSprite) return;
      this.scene.tweens.add({
        targets: this._vorathSprite,
        scaleX: { from: 0.95, to: 1.08 },
        scaleY: { from: 1.08, to: 0.95 },
        duration: FINAL_VORATH_PULSE_SPEED,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });
    this._allTimers.push(vorathPulse);

    // Fade in altars with staggered celestial glow
    for (let i = 0; i < this._altars.length; i++) {
      const altar = this._altars[i];
      altar.sprite.setScale(0.3);
      this.scene.tweens.add({
        targets: altar.sprite,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 600,
        delay: 1000 + i * 250,
        ease: 'Back.easeOut',
      });

      // Subtle glow pulse
      this.scene.tweens.add({
        targets: altar.sprite,
        scaleX: { from: 1.0, to: 1.15 },
        scaleY: { from: 1.0, to: 1.15 },
        duration: 1500,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: 2000 + i * 200,
      });
    }

    // Create the player in the arena
    this.scene.time.delayedCall(1200, () => {
      this._createArenaPlayer();
    });

    // Start arena music + Vorath ambient
    this.scene.time.delayedCall(600, () => {
      try {
        this._arenaMusic = this.scene.sound.add(AUDIO_VORATH_ARENA_MUSIC, {
          volume: getMusicVol(0.4),
          loop: true,
        });
        this._arenaMusic.play();
      } catch (_) {}

      try {
        this._vorathAmbient = this.scene.sound.add(AUDIO_VORATH_MACABRE, {
          volume: getSfxVol(0.15),
          loop: true,
        });
        this._vorathAmbient.play();
      } catch (_) {}
    });

    // After everything is materialized, show Vorath modal (Phase 6)
    const vorathModalTimer = this.scene.time.delayedCall(3000, () => {
      this._phase = 'vorath_modal';
      this._showVorathModal();
    });
    this._allTimers.push(vorathModalTimer);
  }

  /** Create the player entity in the arena */
  _createArenaPlayer() {
    // The scene should create the player at the arena position
    // We call the scene's player creation with the arena grid
    if (this.scene.player) {
      this.scene.player.destroy();
      this.scene.player = null;
    }

    // Store the arena grid on the scene so _createPlayer() can reference it
    this.scene.mazeGrid = this._arenaGrid;
    this.scene.playerStartCol = this._arenaPlayerCol;
    this.scene.playerStartRow = this._arenaPlayerRow;
    this.scene.mazeGridWidth = 2 * this._arenaMazeCols + 1;
    this.scene.mazeGridHeight = 2 * this._arenaMazeRows + 1;

    // Create player via scene method
    this.scene._createPlayer();

    // Set up camera to follow the player in the arena
    const gridCols = 2 * this._arenaMazeCols + 1;
    const gridRows = 2 * this._arenaMazeRows + 1;
    const mazePixelW = gridCols * TILE_SIZE;
    const mazePixelH = gridRows * TILE_SIZE;

    const cam = this.scene.cameras.main;
    cam.setBounds(0, 0, mazePixelW, mazePixelH);
    cam.startFollow(this.scene.player, true, 0.15, 0.15);
  }

  // ===============================================================
  // Phase 6: Vorath Modal
  // ===============================================================

  _showVorathModal() {
    this.scene._transitionLock = true;

    const cam = this.scene.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const elements = [];

    // Dark red overlay (Vorath style)
    const overlay = this.scene.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      COLOR_NARRATOR_VORATH_BG, 0.88,
    );
    overlay.setDepth(300).setAlpha(0);
    elements.push(overlay);

    // Modal dimensions
    const modalW = 400;
    const modalH = 300;
    const modalX = centerX - modalW / 2;
    const modalY = centerY - modalH / 2;

    // Dark red border
    const border = this.scene.add.graphics().setDepth(301).setAlpha(0);
    border.lineStyle(2, 0xcc0000, 0.5);
    border.strokeRect(modalX, modalY, modalW, modalH);
    border.lineStyle(1, 0xff0000, 0.15);
    border.strokeRect(modalX + 3, modalY + 3, modalW - 6, modalH - 6);
    elements.push(border);

    // Speaker name
    const nameColor = `#${COLOR_NARRATOR_VORATH_NAME.toString(16).padStart(6, '0')}`;
    const nameText = this.scene.add.text(centerX, modalY + 24, 'Vorath', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      fontStyle: 'bold',
      color: nameColor,
    }).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(nameText);

    // Decorative line
    const nameLine = this.scene.add.graphics().setDepth(301).setAlpha(0);
    nameLine.lineStyle(1, 0xcc0000, 0.3);
    nameLine.lineBetween(modalX + 30, modalY + 42, modalX + modalW - 30, modalY + 42);
    elements.push(nameLine);

    // Vorath's message
    const textColor = `#${COLOR_NARRATOR_VORATH.toString(16).padStart(6, '0')}`;
    const message =
      'How DARE you enter my domain.\n\n' +
      'Fool. I have consumed the energy of\n' +
      'a thousand souls who wandered these\n' +
      'halls before you.\n\n' +
      'Your precious relics will serve ME.\n' +
      'I will shatter my prison, and then...\n' +
      'I will go after HER.\n\n' +
      'You will be absorbed. Like all the rest.';
    const bodyText = this.scene.add.text(centerX, modalY + 56, message, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: textColor,
      lineSpacing: 5,
      align: 'center',
      fontStyle: 'italic',
    }).setOrigin(0.5, 0).setDepth(301).setAlpha(0);
    elements.push(bodyText);

    // Red vignette edges
    const vignetteTop = this.scene.add.rectangle(
      centerX, modalY + 2, modalW - 8, 6, 0xff0000, 0.06,
    ).setDepth(301).setAlpha(0);
    elements.push(vignetteTop);
    const vignetteBot = this.scene.add.rectangle(
      centerX, modalY + modalH - 2, modalW - 8, 6, 0xff0000, 0.06,
    ).setDepth(301).setAlpha(0);
    elements.push(vignetteBot);

    // Footer
    const footer = this.scene.add.text(
      centerX, modalY + modalH - 26,
      'Press ENTER or SPACE to continue', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#661111',
      },
    ).setOrigin(0.5).setDepth(301).setAlpha(0);
    elements.push(footer);
    this.scene.tweens.add({
      targets: footer,
      alpha: { from: 0.3, to: 0.8 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 400,
    });

    // Vorath sound
    try { this.scene.sound.play(AUDIO_VORATH_MACABRE, { volume: getSfxVol(0.4) }); } catch (_) {}

    // Fade in
    this.scene.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 350,
      ease: 'Quad.easeOut',
    });
    for (const el of elements) {
      if (el === overlay) continue;
      this.scene.tweens.add({
        targets: el,
        alpha: 1,
        duration: 350,
        delay: 150,
        ease: 'Quad.easeOut',
      });
    }

    // Dismiss
    const dismiss = () => {
      this.scene.input.keyboard.off('keydown-ENTER', dismiss);
      this.scene.input.keyboard.off('keydown-SPACE', dismiss);

      try { this.scene.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

      for (const el of elements) {
        this.scene.tweens.add({
          targets: el,
          alpha: 0,
          duration: 250,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el && el.destroy) el.destroy();
          },
        });
      }

      // Start battle (Phase 7)
      this.scene.time.delayedCall(350, () => {
        this._phase = 'battle';
        this.scene._transitionLock = false;
        this._startBattle();
      });
    };

    this.scene.time.delayedCall(600, () => {
      this.scene.input.keyboard.on('keydown-ENTER', dismiss);
      this.scene.input.keyboard.on('keydown-SPACE', dismiss);
    });
  }

  // ===============================================================
  // Phase 7: Battle — Vorath's Dark Spheres
  // ===============================================================

  _startBattle() {
    // Create physics group for dark spheres
    this._sphereGroup = this.scene.physics.add.group();
    this._currentSphereInterval = FINAL_VORATH_SPHERE_INITIAL_INTERVAL;

    // Start altar proximity detection
    this._startAltarProximityCheck();

    // Launch first sphere, then keep launching
    this._launchDarkSphere();
    this._scheduleSphere();
  }

  _scheduleSphere() {
    if (this._phase !== 'battle') return;

    this._sphereTimer = this.scene.time.delayedCall(this._currentSphereInterval, () => {
      if (this._phase !== 'battle') return;
      this._launchDarkSphere();
      // Accelerate
      this._currentSphereInterval = Math.max(
        this._currentSphereInterval * FINAL_VORATH_SPHERE_ACCEL,
        FINAL_VORATH_SPHERE_MIN_INTERVAL,
      );
      this._scheduleSphere();
    });
    this._allTimers.push(this._sphereTimer);
  }

  _launchDarkSphere() {
    if (!this.scene.player || !this._vorathSprite) return;

    // Sound
    try { this.scene.sound.play(AUDIO_VORATH_SPHERE_LAUNCH, { volume: getSfxVol(0.35) }); } catch (_) {}

    // Calculate base direction toward player's CURRENT position
    const targetX = this.scene.player.x;
    const targetY = this.scene.player.y;
    const dx = targetX - this._vorathPx;
    const dy = targetY - this._vorathPy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 1) return; // safety

    const baseAngle = Math.atan2(dy, dx);

    // Determine how many spheres to launch and how many are homing.
    //   0 altars activated → 2 spheres (1 direct + 1 homing)
    //   1-2 altars activated → 3 spheres (1 direct + 2 homing)
    //   3+ altars activated → 4 spheres (1 direct + 3 homing)
    let sphereCount = 2;
    let homingCount = 1;
    if (this._altarsActivated >= 3) {
      sphereCount = 4;
      homingCount = 3;
    } else if (this._altarsActivated >= 1) {
      sphereCount = 3;
      homingCount = 2;
    }

    // Angular spread for extra spheres (radians)
    const spreadAngle = Math.PI / 6; // 30 degrees

    // Build launch list: first sphere is always direct (non-homing),
    // remaining spheres are spread at angular offsets and the last
    // `homingCount` of them are homing.
    for (let i = 0; i < sphereCount; i++) {
      let angle = baseAngle;
      let isHoming = false;

      if (i > 0) {
        // Alternate angular offsets: +spread, -spread, +2×spread, ...
        const sign = (i % 2 === 1) ? 1 : -1;
        const step = Math.ceil(i / 2);
        angle = baseAngle + sign * spreadAngle * step;
      }

      // The last `homingCount` spheres are homing
      if (i >= sphereCount - homingCount) {
        isHoming = true;
      }

      this._createSphere(angle, isHoming);
    }
  }

  /**
   * Create a single dark sphere at Vorath's position, heading at the
   * given angle. If `isHoming` is true, the sphere will track the
   * player each frame at a slower speed.
   * @param {number} angle - Launch angle in radians
   * @param {boolean} isHoming - Whether this sphere tracks the player
   */
  _createSphere(angle, isHoming) {
    const sphere = this.scene.physics.add.sprite(
      this._vorathPx, this._vorathPy, TEX_DARK_SPHERE,
    );
    sphere.setDepth(15);
    this._sphereGroup.add(sphere);
    this._allElements.push(sphere);

    // Speed: homing spheres are slower
    const speed = isHoming ? FINAL_VORATH_HOMING_SPHERE_SPEED : FINAL_VORATH_SPHERE_SPEED;
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;
    sphere.setVelocity(vx, vy);

    // Rotation effect
    this.scene.tweens.add({
      targets: sphere,
      angle: 360,
      duration: 800,
      repeat: -1,
    });

    // Overlap with player (damage)
    this.scene.physics.add.overlap(sphere, this.scene.player, () => {
      if (this._playerInvulnerable) return;
      this._onSphereHitPlayer(sphere);
    }, null, this);

    // Sphere flags
    sphere._isActive = true;
    sphere._isHoming = isHoming;

    // Homing spheres get a visual tint and a grace-period timestamp;
    // during the grace period they fly straight before tracking begins.
    if (isHoming) {
      sphere.setTint(0xff4444);
      sphere._homingStartTime = this.scene.time.now;
    }

    // Auto-destroy if it goes too far off screen
    this.scene.time.delayedCall(10000, () => {
      if (sphere && sphere._isActive) {
        sphere._isActive = false;
        sphere.destroy();
      }
    });
  }

  _onSphereHitPlayer(sphere) {
    if (!sphere._isActive) return;
    sphere._isActive = false;

    // Damage player
    try { this.scene.sound.play(AUDIO_VORATH_SPHERE_HIT, { volume: getSfxVol(0.5) }); } catch (_) {}

    if (this.healthSystem) {
      this.healthSystem.takeDamage(FINAL_VORATH_SPHERE_DAMAGE);
    }

    // Red flash on camera
    this.scene.cameras.main.flash(200, 255, 0, 0);

    // Set invulnerability to prevent rapid consecutive hits
    this._playerInvulnerable = true;

    // Blinking effect during invulnerability
    if (this.scene.player) {
      const blinkTween = this.scene.tweens.add({
        targets: this.scene.player,
        alpha: { from: 1, to: 0.3 },
        duration: 100,
        yoyo: true,
        repeat: Math.floor(FINAL_VORATH_INVULN_DURATION / 200) - 1,
        onComplete: () => {
          if (this.scene.player) this.scene.player.setAlpha(1);
        },
      });
      this._allTweens.push(blinkTween);
    }

    // Reset invulnerability after duration
    const invulnTimer = this.scene.time.delayedCall(FINAL_VORATH_INVULN_DURATION, () => {
      this._playerInvulnerable = false;
    });
    this._allTimers.push(invulnTimer);

    // Destroy sphere
    sphere.destroy();
  }

  /**
   * Called from the scene's update loop to check sphere-wall collisions.
   * Spheres destroy interior walls they touch.
   */
  updateSpheres() {
    if (!this._sphereGroup || this._phase !== 'battle') return;

    const spheres = this._sphereGroup.getChildren();
    const gridCols = 2 * this._arenaMazeCols + 1;
    const gridRows = 2 * this._arenaMazeRows + 1;

    for (let i = spheres.length - 1; i >= 0; i--) {
      const sphere = spheres[i];
      if (!sphere || !sphere._isActive) continue;

      // Get the sphere's grid position
      const gc = Math.floor(sphere.x / TILE_SIZE);
      const gr = Math.floor(sphere.y / TILE_SIZE);

      // Check if out of bounds — destroy
      if (gc < 0 || gc >= gridCols || gr < 0 || gr >= gridRows) {
        sphere._isActive = false;
        sphere.destroy();
        continue;
      }

      // Check if on a wall cell
      if (this._arenaGrid[gr] && this._arenaGrid[gr][gc] === CellType.WALL) {
        // Check if it's an exterior wall (row 0, col 0, last row, last col)
        const isExterior = gr === 0 || gr === gridRows - 1 || gc === 0 || gc === gridCols - 1;

        // Check if it's a Vorath core wall — spheres pass through
        // (they originate from Vorath, so the core is transparent to them)
        const cb = this._vorathCoreBounds;
        const isVorathCore = cb && gc >= cb.minGc && gc <= cb.maxGc && gr >= cb.minGr && gr <= cb.maxGr;

        if (isVorathCore) {
          // Sphere passes through Vorath's body — no collision
          continue;
        } else if (isExterior) {
          // Destroy sphere, keep wall
          sphere._isActive = false;
          sphere.destroy();
        } else if (this._protectedWallCells && this._protectedWallCells.has(`${gr},${gc}`)) {
          // Wall adjacent to an altar zone — indestructible.
          // Destroy the sphere, keep the wall intact so the altar
          // areas maintain their structural integrity.
          sphere._isActive = false;
          sphere.destroy();
        } else {
          // Destroy interior wall
          this._arenaGrid[gr][gc] = CellType.PATH;

          // Find and remove the wall sprite at this position
          const wallX = gc * TILE_SIZE + TILE_SIZE / 2;
          const wallY = gr * TILE_SIZE + TILE_SIZE / 2;
          const walls = this._arenaWallGroup ? this._arenaWallGroup.getChildren() : [];
          for (const wall of walls) {
            if (Math.abs(wall.x - wallX) < 2 && Math.abs(wall.y - wallY) < 2) {
              // Wall destruction effect
              try { this.scene.sound.play(AUDIO_VORATH_SPHERE_WALL, { volume: getSfxVol(0.25) }); } catch (_) {}

              // Brief flash at wall position
              const wallFlash = this.scene.add.circle(wallX, wallY, TILE_SIZE * 0.6, 0xffffff, 0.6);
              wallFlash.setDepth(20);
              this.scene.tweens.add({
                targets: wallFlash,
                alpha: 0,
                scaleX: 1.5,
                scaleY: 1.5,
                duration: 300,
                onComplete: () => wallFlash.destroy(),
              });

              wall.destroy();
              break;
            }
          }

          // Destroy sphere after wall hit
          sphere._isActive = false;
          sphere.destroy();
        }
      }
    }

    // Check Vorath contact damage and homing sphere tracking
    this._checkVorathContact();
    this._updateHomingSpheres(spheres);
  }

  /**
   * Per-frame check: if the player is too close to Vorath's mass,
   * deal contact damage and repel them outward.
   */
  _checkVorathContact() {
    if (this._playerInvulnerable) return;
    if (!this.scene.player || !this._vorathSprite) return;
    if (this._phase !== 'battle') return;

    const px = this.scene.player.x;
    const py = this.scene.player.y;
    const dx = px - this._vorathPx;
    const dy = py - this._vorathPy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist >= FINAL_VORATH_CONTACT_RADIUS) return;

    // Deal contact damage
    try { this.scene.sound.play(AUDIO_VORATH_SPHERE_HIT, { volume: getSfxVol(0.5) }); } catch (_) {}

    if (this.healthSystem) {
      this.healthSystem.takeDamage(FINAL_VORATH_CONTACT_DAMAGE);
    }

    // Red camera flash
    this.scene.cameras.main.flash(FINAL_VORATH_REPEL_DURATION, 255, 0, 0);

    // Set invulnerability (prevents repeated hits + sphere hits during repulsion)
    this._playerInvulnerable = true;

    // Blinking effect during invulnerability
    const blinkTween = this.scene.tweens.add({
      targets: this.scene.player,
      alpha: { from: 1, to: 0.3 },
      duration: 100,
      yoyo: true,
      repeat: Math.floor(FINAL_VORATH_INVULN_DURATION / 200) - 1,
      onComplete: () => {
        if (this.scene.player) this.scene.player.setAlpha(1);
      },
    });
    this._allTweens.push(blinkTween);

    // Reset invulnerability after duration
    const invulnTimer = this.scene.time.delayedCall(FINAL_VORATH_INVULN_DURATION, () => {
      this._playerInvulnerable = false;
    });
    this._allTimers.push(invulnTimer);

    // Repel player away from Vorath
    const repelDist = TILE_SIZE * 2; // push 2 tiles away
    const normDx = dist > 0 ? dx / dist : 0;
    const normDy = dist > 0 ? dy / dist : 1;
    const targetX = this._vorathPx + normDx * (FINAL_VORATH_CONTACT_RADIUS + repelDist);
    const targetY = this._vorathPy + normDy * (FINAL_VORATH_CONTACT_RADIUS + repelDist);

    const repelTween = this.scene.tweens.add({
      targets: this.scene.player,
      x: targetX,
      y: targetY,
      duration: FINAL_VORATH_REPEL_DURATION,
      ease: 'Power2',
      onComplete: () => {
        // Snap player grid coords to nearest walkable cell
        if (this.scene.player) {
          const newGc = Math.round((this.scene.player.x - TILE_SIZE / 2) / TILE_SIZE);
          const newGr = Math.round((this.scene.player.y - TILE_SIZE / 2) / TILE_SIZE);
          this.scene.player.gridCol = newGc;
          this.scene.player.gridRow = newGr;
          // Snap to grid center
          this.scene.player.x = newGc * TILE_SIZE + TILE_SIZE / 2;
          this.scene.player.y = newGr * TILE_SIZE + TILE_SIZE / 2;
        }
      },
    });
    this._allTweens.push(repelTween);
  }

  /**
   * Update homing spheres to track the player each frame.
   * @param {Phaser.GameObjects.Sprite[]} spheres - Active sphere array
   */
  _updateHomingSpheres(spheres) {
    if (!this.scene.player) return;
    const playerX = this.scene.player.x;
    const playerY = this.scene.player.y;

    const now = this.scene.time.now;

    for (let i = spheres.length - 1; i >= 0; i--) {
      const sphere = spheres[i];
      if (!sphere || !sphere._isActive || !sphere._isHoming) continue;

      // During the grace period, sphere flies straight at its initial
      // angular offset — skip homing recalculation so spheres spread out
      if ((now - sphere._homingStartTime) < FINAL_VORATH_HOMING_GRACE_MS) continue;

      // Recalculate velocity toward player at homing speed
      const dx = playerX - sphere.x;
      const dy = playerY - sphere.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 1) continue;

      const vx = (dx / dist) * FINAL_VORATH_HOMING_SPHERE_SPEED;
      const vy = (dy / dist) * FINAL_VORATH_HOMING_SPHERE_SPEED;
      sphere.setVelocity(vx, vy);
    }
  }

  // ===============================================================
  // Phase 8: Altar Activation
  // ===============================================================

  _startAltarProximityCheck() {
    this._altarCheckEvent = this.scene.time.addEvent({
      delay: 150,
      loop: true,
      callback: () => {
        if (this._phase !== 'battle') return;
        if (!this.scene.player) return;

        // Player expanded-grid coords (updated only when tween completes)
        const pgc = this.scene.player.gridCol;
        const pgr = this.scene.player.gridRow;

        for (const altar of this._altars) {
          if (altar.activated) continue;

          // Altar expanded-grid coords (center cell)
          const agc = altar.col * 2 + 1;
          const agr = altar.row * 2 + 1;

          // Distance in expanded grid cells (each cell = 1 TILE_SIZE).
          // The altar is 48px = 2×2 tiles, so its visual body extends
          // ±1 cell from center. We allow activation when the player
          // is within Manhattan distance ≤ 2 of the altar center,
          // meaning the player can be ON the altar body or 1 cell away
          // from its visual edge.
          const adx = Math.abs(pgc - agc);
          const ady = Math.abs(pgr - agr);
          const manhattan = adx + ady;

          if (manhattan > 2) continue;

          // Player must be on a walkable cell
          if (this._arenaGrid &&
              this._arenaGrid[pgr] &&
              this._arenaGrid[pgr][pgc] !== CellType.PATH) {
            continue;
          }

          this._activateAltar(altar);
        }
      },
    });
    this._allTimers.push(this._altarCheckEvent);
  }

  _activateAltar(altar) {
    altar.activated = true;
    this._altarsActivated++;

    // Lock input briefly
    this.scene._transitionLock = true;

    // Sound
    try { this.scene.sound.play(AUDIO_ALTAR_ACTIVATE, { volume: getSfxVol(0.5) }); } catch (_) {}

    // Decrement agmuth count
    this.scene._agmuthCount = Math.max(0, this.scene._agmuthCount - 1);
    // Emit AGMUTH_DEACTIVATED (not AGMUTH_CHANGED) so that the HUD plays
    // the disintegration animation — the relic is consumed to power the altar.
    this.eventBus.emit(Events.AGMUTH_DEACTIVATED, this.scene._agmuthCount);
    this.eventBus.emit(Events.FINAL_ALTAR_ACTIVATED, this._altarsActivated);

    // Visual: altar glows much brighter with golden sparkles.
    // Scale to 1.25 (48px → 60px) — majestic but not oversized.
    this.scene.tweens.add({
      targets: altar.sprite,
      scaleX: 1.25,
      scaleY: 1.25,
      duration: 500,
      ease: 'Back.easeOut',
    });

    // Golden sparkle particles — wider spread for the 48px altar
    const sparkles = this.scene.add.particles(altar.px, altar.py, TEX_GLOW, {
      lifespan: { min: 700, max: 1400 },
      speed: { min: 40, max: 120 },
      scale: { start: 0.6, end: 0 },
      alpha: { start: 1.0, end: 0 },
      tint: COLOR_ALTAR_GOLD,
      frequency: 60,
      quantity: 4,
      emitting: true,
    });
    sparkles.setDepth(12);
    this._allElements.push(sparkles);

    // Brief camera flash
    this.scene.cameras.main.flash(300, 255, 215, 0); // golden flash

    // Unlock after activation animation
    this.scene.time.delayedCall(600, () => {
      this.scene._transitionLock = false;

      // Check if all altars activated
      if (this._altarsActivated >= 4) {
        this._onAllAltarsActivated();
      }
    });
  }

  // ===============================================================
  // Phase 9: Vorath Destruction
  // ===============================================================

  _onAllAltarsActivated() {
    this._phase = 'altars_done';
    this.scene._transitionLock = true;

    // Stop sphere launching
    if (this._sphereTimer) {
      this._sphereTimer.remove();
      this._sphereTimer = null;
    }
    // Stop altar check
    if (this._altarCheckEvent) {
      this._altarCheckEvent.remove();
      this._altarCheckEvent = null;
    }

    // Destroy remaining spheres
    if (this._sphereGroup) {
      this._sphereGroup.clear(true, true);
    }

    // Player becomes invulnerable
    this._playerInvulnerable = true;

    // Camera flash
    this.scene.cameras.main.flash(500, 255, 215, 0);

    // Wait a moment, then start beam sequence
    this.scene.time.delayedCall(1000, () => {
      this._fireAltarBeams();
    });
  }

  _fireAltarBeams() {
    // Fire beams one by one from each altar to Vorath.
    // Each beam impacts at a DIFFERENT offset from Vorath's center,
    // and produces escalating damage VFX (particles, smoke, vibration, debris).
    let beamIndex = 0;
    this._beamElements = []; // track beam graphics + emitters for cleanup on explosion

    // Define unique impact offsets for each beam so they don't converge
    // on the same spot. Vorath is 192px, so offsets are within ~70px of center.
    const impactOffsets = [
      { dx: -35, dy: -25 }, // top-left area
      { dx: 30, dy: -30 },  // top-right area
      { dx: -25, dy: 35 },  // bottom-left area
      { dx: 40, dy: 20 },   // bottom-right area
    ];

    const fireNext = () => {
      if (beamIndex >= this._altars.length) {
        // All beams fired — wait for player proximity
        this.scene.time.delayedCall(500, () => {
          this._waitForPlayerProximity();
        });
        return;
      }

      const altar = this._altars[beamIndex];
      const offset = impactOffsets[beamIndex];
      const intensity = beamIndex + 1; // 1, 2, 3, 4 — escalating
      beamIndex++;

      // Impact point (offset from Vorath center)
      const impactX = this._vorathPx + offset.dx;
      const impactY = this._vorathPy + offset.dy;

      // Sound — each beam gets its own looped sound, all play simultaneously
      try {
        const beamSnd = this.scene.sound.add(AUDIO_ALTAR_BEAM, {
          volume: getSfxVol(0.35 + intensity * 0.08),
          loop: true,
        });
        beamSnd.play();
        this._beamSounds.push(beamSnd);
      } catch (_) {}

      // Draw beam line from altar to impact point on Vorath
      const beam = this.scene.add.graphics().setDepth(20);
      beam.lineStyle(2 + intensity, COLOR_ALTAR_GOLD, 0.9);
      beam.lineBetween(altar.px, altar.py, impactX, impactY);
      this._allElements.push(beam);
      this._beamElements.push(beam);

      // Beam line particles (scattered along the beam path)
      const midX = (altar.px + impactX) / 2;
      const midY = (altar.py + impactY) / 2;
      const beamEmitter = this.scene.add.particles(midX, midY, TEX_BEAM_PARTICLE, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 10, max: 40 },
        scale: { start: 0.6, end: 0 },
        alpha: { start: 0.8, end: 0 },
        tint: COLOR_ALTAR_GOLD,
        frequency: 60,
        quantity: 1 + intensity,
      });
      beamEmitter.setDepth(21);
      this._allElements.push(beamEmitter);
      this._beamElements.push(beamEmitter);

      // Pulse beam visibility
      this.scene.tweens.add({
        targets: beam,
        alpha: { from: 0.4, to: 1 },
        duration: 150 + Math.random() * 100,
        yoyo: true,
        repeat: -1,
      });

      // ── Damage VFX at impact point ──

      // Impact spark particles (golden fire)
      const sparkEmitter = this.scene.add.particles(impactX, impactY, TEX_GLOW, {
        lifespan: { min: 200, max: 500 + intensity * 100 },
        speed: { min: 30 + intensity * 10, max: 80 + intensity * 20 },
        scale: { start: 0.3 + intensity * 0.1, end: 0 },
        alpha: { start: 1, end: 0 },
        tint: [COLOR_ALTAR_GOLD, COLOR_CELESTIAL_ORANGE, 0xffffff],
        frequency: 40 - intensity * 5,
        quantity: 1 + intensity,
      });
      sparkEmitter.setDepth(22);
      this._allElements.push(sparkEmitter);
      this._beamElements.push(sparkEmitter);

      // Smoke at impact point
      const smokeEmitter = this.scene.add.particles(impactX, impactY, TEX_SMOKE, {
        lifespan: { min: 600, max: 1200 + intensity * 300 },
        speed: { min: 8, max: 25 + intensity * 5 },
        angle: { min: 200, max: 340 },
        scale: { start: 0.3 + intensity * 0.1, end: 0 },
        alpha: { start: 0.3 + intensity * 0.05, end: 0 },
        frequency: 80 - intensity * 10,
        quantity: 1 + Math.floor(intensity / 2),
      });
      smokeEmitter.setDepth(11);
      this._allElements.push(smokeEmitter);
      this._beamElements.push(smokeEmitter);

      // Vorath debris chunks — small pieces flying off
      const debrisEmitter = this.scene.add.particles(impactX, impactY, TEX_VORATH_MASS, {
        lifespan: { min: 500, max: 1000 + intensity * 200 },
        speed: { min: 40 + intensity * 15, max: 100 + intensity * 25 },
        scale: { start: 0.03 + intensity * 0.01, end: 0 },
        alpha: { start: 0.9, end: 0 },
        rotate: { min: 0, max: 360 },
        frequency: 300 - intensity * 40,
        quantity: 1,
      });
      debrisEmitter.setDepth(22);
      this._allElements.push(debrisEmitter);
      this._beamElements.push(debrisEmitter);

      // Vorath vibration — shake increases with each beam
      if (this._vorathSprite) {
        // Stop any previous vibration tween
        if (this._vorathVibrateTween) {
          this._vorathVibrateTween.stop();
        }
        const vibIntensity = intensity * 0.8;
        this._vorathVibrateTween = this.scene.tweens.add({
          targets: this._vorathSprite,
          x: { from: this._vorathPx - vibIntensity, to: this._vorathPx + vibIntensity },
          duration: 40 + Math.random() * 20,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }

      // Camera rumble proportional to beam count
      this.scene.cameras.main.shake(400, 0.003 * intensity);

      // Next beam after interval
      this.scene.time.delayedCall(FINAL_ALTAR_BEAM_INTERVAL, fireNext);
    };

    fireNext();
  }

  _waitForPlayerProximity() {
    // Unlock player movement — they need to approach Vorath
    this.scene._transitionLock = false;

    // Show hint text
    const cam = this.scene.cameras.main;
    const hintText = this.scene.add.text(
      cam.scrollX + cam.width / 2,
      cam.scrollY + 40,
      'Approach Vorath...', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '14px',
        color: '#ffd700',
        fontStyle: 'bold',
      },
    ).setOrigin(0.5).setDepth(50).setScrollFactor(0);
    this._allElements.push(hintText);

    this.scene.tweens.add({
      targets: hintText,
      alpha: { from: 0.5, to: 1 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Check for proximity every frame
    this._proximityCheckEvent = this.scene.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => {
        if (!this.scene.player) return;

        // Convert player grid coords (odd indices) to logical coords
        const playerCol = Math.floor(this.scene.player.gridCol / 2);
        const playerRow = Math.floor(this.scene.player.gridRow / 2);

        const dc = Math.abs(playerCol - this._vorathPrisonCenter.col);
        const dr = Math.abs(playerRow - this._vorathPrisonCenter.row);

        if (dc <= FINAL_PLAYER_PROXIMITY_TRIGGER && dr <= FINAL_PLAYER_PROXIMITY_TRIGGER) {
          this._proximityCheckEvent.remove();
          this._proximityCheckEvent = null;
          if (hintText && hintText.destroy) hintText.destroy();

          // Lock player input
          this.scene._transitionLock = true;

          // Player sphere glows intensely for 2 seconds with energy sound,
          // then the glow fades and Vorath explodes.
          this._playerGlow(() => {
            this._destroyVorath();
          });
        }
      },
    });
    this._allTimers.push(this._proximityCheckEvent);
  }

  /**
   * Dramatic 2-second white/gold player glow buildup.
   * The player sphere intensifies with light and particles, an energy sound
   * plays, and after 2 seconds the glow fades. Then the callback fires
   * (used to trigger Vorath's destruction).
   * @private
   * @param {Function} onComplete - Called after the glow sequence ends.
   */
  _playerGlow(onComplete) {
    if (!this.scene.player) {
      if (onComplete) onComplete();
      return;
    }

    const player = this.scene.player;
    const glowDuration = 2000;

    // Energy buildup sound
    try { this.scene.sound.play(AUDIO_FINAL_ENERGY, { volume: getSfxVol(0.5) }); } catch (_) {}

    // ── White/golden halo around the player ──
    const halo = this.scene.add.graphics();
    halo.setDepth(player.depth - 1);
    this._allElements.push(halo);

    // Draw initial halo
    const drawHalo = (radius, alpha) => {
      halo.clear();
      halo.fillStyle(0xffffff, alpha * 0.3);
      halo.fillCircle(player.x, player.y, radius);
      halo.fillStyle(COLOR_CELESTIAL_GOLD, alpha * 0.5);
      halo.fillCircle(player.x, player.y, radius * 0.7);
    };

    // ── Glow particle emitter (white + gold, intensifies) ──
    const glowEmitter = this.scene.add.particles(
      player.x, player.y, TEX_GLOW, {
        follow: player,
        lifespan: { min: 300, max: 700 },
        speed: { min: 15, max: 50 },
        scale: { start: 0.5, end: 0 },
        alpha: { start: 0.9, end: 0 },
        tint: [0xffffff, COLOR_CELESTIAL_GOLD, 0xffffcc],
        frequency: 50,
        quantity: 3,
        emitting: true,
      },
    );
    glowEmitter.setDepth(player.depth + 1);
    this._allElements.push(glowEmitter);

    // ── Player sphere tint cycle (white ↔ gold) ──
    player.setTint(0xffffff);
    const tintCycle = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 300,
      yoyo: true,
      repeat: -1,
      onUpdate: (tween) => {
        const v = tween.getValue();
        // Blend between white and gold
        const r = 255;
        const g = Math.round(255 - v * 40);   // 255 → 215
        const b = Math.round(255 - v * 255);   // 255 → 0
        // eslint-disable-next-line no-bitwise
        player.setTint((r << 16) | (g << 8) | b);
      },
    });

    // ── Intensify halo over 2 seconds via update loop ──
    const startTime = this.scene.time.now;
    const haloUpdate = this.scene.time.addEvent({
      delay: 30,
      loop: true,
      callback: () => {
        const elapsed = this.scene.time.now - startTime;
        const t = Math.min(elapsed / glowDuration, 1); // 0→1
        const radius = 14 + t * 26;   // 14px → 40px
        const alpha = 0.2 + t * 0.7;  // 0.2 → 0.9
        drawHalo(radius, alpha);

        // Increase particle output as glow intensifies
        glowEmitter.setFrequency(Math.max(15, 50 - t * 35));
        glowEmitter.setQuantity(3 + Math.floor(t * 5));
      },
    });
    this._allTimers.push(haloUpdate);

    // ── After 2 seconds: stop glow, then call back ──
    this.scene.time.delayedCall(glowDuration, () => {
      // Stop the halo update
      haloUpdate.remove();

      // Stop tint cycle and restore player tint
      tintCycle.stop();
      player.clearTint();

      // Stop glow emitter
      glowEmitter.stop();

      // Fade out halo
      this.scene.tweens.add({
        targets: halo,
        alpha: 0,
        duration: 300,
        onComplete: () => {
          halo.destroy();
        },
      });

      // Brief pause then trigger explosion
      this.scene.time.delayedCall(400, () => {
        if (onComplete) onComplete();
      });
    });
  }

  _destroyVorath() {
    this._phase = 'destruction';

    // ── Destroy all beam elements (beams, particles, smoke, debris) ──
    if (this._beamElements) {
      for (const el of this._beamElements) {
        if (el && !el.destroyed) {
          // Stop emitters before destroying
          if (typeof el.stop === 'function') el.stop();
          try { el.destroy(); } catch (_) {}
        }
      }
      this._beamElements = [];
    }

    // Stop all looped beam sounds
    if (this._beamSounds) {
      for (const snd of this._beamSounds) {
        try { snd.stop(); snd.destroy(); } catch (_) {}
      }
      this._beamSounds = [];
    }

    // Stop Vorath vibration tween
    if (this._vorathVibrateTween) {
      this._vorathVibrateTween.stop();
      this._vorathVibrateTween = null;
    }

    // Sound
    try { this.scene.sound.play(AUDIO_VORATH_DESTROY, { volume: getSfxVol(0.7) }); } catch (_) {}

    // Stop arena music
    if (this._arenaMusic) {
      this._arenaMusic.stop();
      this._arenaMusic.destroy();
      this._arenaMusic = null;
    }
    if (this._vorathAmbient) {
      this._vorathAmbient.stop();
      this._vorathAmbient.destroy();
      this._vorathAmbient = null;
    }

    // Camera shake
    this.scene.cameras.main.shake(FINAL_VORATH_DESTROY_DURATION, 0.02);

    // Vorath explosion — scale up, flash, dissolve
    if (this._vorathSprite) {
      // Reset position in case vibration tween left it offset
      this._vorathSprite.setPosition(this._vorathPx, this._vorathPy);
      // Flash white
      this._vorathSprite.setTint(0xffffff);

      this.scene.tweens.add({
        targets: this._vorathSprite,
        scaleX: 3,
        scaleY: 3,
        alpha: 0,
        duration: FINAL_VORATH_DESTROY_DURATION,
        ease: 'Quad.easeOut',
      });
    }

    // Explosion particles — massive burst
    const explosionEmitter = this.scene.add.particles(
      this._vorathPx, this._vorathPy, TEX_GLOW, {
        lifespan: { min: 800, max: 2000 },
        speed: { min: 80, max: 250 },
        scale: { start: 1.2, end: 0 },
        alpha: { start: 1.0, end: 0 },
        tint: [0xffffff, COLOR_CELESTIAL_GOLD, COLOR_CELESTIAL_ORANGE, 0xffddaa],
        frequency: 10,
        quantity: 12,
        emitting: true,
      },
    );
    explosionEmitter.setDepth(100);
    this._allElements.push(explosionEmitter);

    // Smoke particles
    const smokeEmitter = this.scene.add.particles(
      this._vorathPx, this._vorathPy, TEX_SMOKE, {
        lifespan: { min: 1000, max: 2500 },
        speed: { min: 20, max: 60 },
        scale: { start: 0.8, end: 0 },
        alpha: { start: 0.5, end: 0 },
        frequency: 30,
        quantity: 4,
        emitting: true,
      },
    );
    smokeEmitter.setDepth(99);
    this._allElements.push(smokeEmitter);

    // Screen-filling white light
    const cam = this.scene.cameras.main;
    const destroyFlash = this.scene.add.rectangle(
      cam.scrollX + cam.width / 2,
      cam.scrollY + cam.height / 2,
      cam.width * 2, cam.height * 2,
      0xffffff, 0,
    );
    destroyFlash.setDepth(200);
    this._allElements.push(destroyFlash);

    this.scene.tweens.add({
      targets: destroyFlash,
      alpha: { from: 0, to: 0.9 },
      duration: FINAL_VORATH_DESTROY_DURATION * 0.6,
      yoyo: true,
      ease: 'Quad.easeInOut',
    });

    // After destruction animation, emit event and proceed to Final Threia
    this.scene.time.delayedCall(FINAL_VORATH_DESTROY_DURATION + 500, () => {
      // Stop explosion particles
      if (explosionEmitter) explosionEmitter.stop();
      if (smokeEmitter) smokeEmitter.stop();

      this.eventBus.emit(Events.FINAL_VORATH_DESTROYED);

      // Celestial sound
      try { this.scene.sound.play(AUDIO_FINAL_CELESTIAL, { volume: getSfxVol(0.5) }); } catch (_) {}

      this.scene.time.delayedCall(1500, () => {
        this._phase = 'final_threia';
        this._showFinalThreiaModal();
      });
    });
  }

  // ===============================================================
  // Phase 10: Final Threia Modal — the game ending
  // ===============================================================

  _showFinalThreiaModal() {
    const cam = this.scene.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;
    const elements = [];

    // Final music
    try {
      this._finalMusic = this.scene.sound.add(AUDIO_FINAL_MUSIC, {
        volume: getMusicVol(0.4),
        loop: true,
      });
      this._finalMusic.play();
    } catch (_) {}

    // Dark overlay
    const overlay = this.scene.add.rectangle(
      centerX, centerY,
      cam.width * 2, cam.height * 2,
      0x000000, 0.85,
    );
    overlay.setDepth(300).setAlpha(0);
    elements.push(overlay);

    // Modal dimensions
    const modalW = 430;
    const modalH = 360;
    const modalX = centerX - modalW / 2;
    const modalY = centerY - modalH / 2;

    // Deep blue-black background (Threia style)
    const bg = this.scene.add.rectangle(
      centerX, centerY,
      modalW, modalH,
      COLOR_THREIA_MODAL_BG, 0.95,
    );
    bg.setDepth(501).setAlpha(0);
    elements.push(bg);

    // Triple golden border
    const border = this.scene.add.graphics().setDepth(502).setAlpha(0);
    border.lineStyle(3, COLOR_THREIA_MODAL_BORDER, 0.9);
    border.strokeRect(modalX, modalY, modalW, modalH);
    border.lineStyle(1.5, COLOR_THREIA_MODAL_BORDER, 0.4);
    border.strokeRect(modalX + 5, modalY + 5, modalW - 10, modalH - 10);
    border.lineStyle(1, COLOR_THREIA_MODAL_BORDER, 0.15);
    border.strokeRect(modalX + 9, modalY + 9, modalW - 18, modalH - 18);
    elements.push(border);

    // Pulsing corner glows
    for (let i = 0; i < 4; i++) {
      const cx = i < 2 ? modalX + 3 : modalX + modalW - 3;
      const cy = i % 2 === 0 ? modalY + 3 : modalY + modalH - 3;
      const cornerGlow = this.scene.add.circle(cx, cy, 5, COLOR_THREIA_MODAL_BORDER, 0.5);
      cornerGlow.setDepth(503).setAlpha(0);
      elements.push(cornerGlow);
      this.scene.tweens.add({
        targets: cornerGlow,
        alpha: { from: 0.2, to: 0.8 },
        duration: 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
        delay: i * 250,
      });
    }

    // Speaker name
    const nameColor = `#${COLOR_THREIA_MODAL_NAME.toString(16).padStart(6, '0')}`;
    const nameText = this.scene.add.text(centerX, modalY + 28, 'Threia', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '18px',
      fontStyle: 'bold',
      color: nameColor,
    }).setOrigin(0.5).setDepth(503).setAlpha(0);
    elements.push(nameText);

    // Decorative line
    const nameLine = this.scene.add.graphics().setDepth(503).setAlpha(0);
    nameLine.lineStyle(1, COLOR_THREIA_MODAL_BORDER, 0.5);
    nameLine.lineBetween(modalX + 40, modalY + 48, modalX + modalW - 40, modalY + 48);
    elements.push(nameLine);

    // Final message
    const textColor = `#${COLOR_THREIA_MODAL_TEXT.toString(16).padStart(6, '0')}`;
    const message =
      'You have done it, brave soul.\n\n' +
      'You are the first to reach beyond\n' +
      'the labyrinth in a very long time.\n' +
      'Your courage has opened the path\n' +
      'for others to find their rest.\n\n' +
      'I do not know what lies beyond...\n' +
      'but I believe it will be beautiful.\n\n' +
      'Life is energy. Energy is life.\n' +
      'Your essence will remain with those\n' +
      'you knew and loved. Always.';
    const bodyText = this.scene.add.text(centerX, modalY + 60, message, {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '12px',
      color: textColor,
      lineSpacing: 5,
      align: 'center',
    }).setOrigin(0.5, 0).setDepth(503).setAlpha(0);
    elements.push(bodyText);

    // Footer
    const footer = this.scene.add.text(
      centerX, modalY + modalH - 26,
      'Press ENTER or SPACE to continue', {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#8888aa',
      },
    ).setOrigin(0.5).setDepth(503).setAlpha(0);
    elements.push(footer);
    this.scene.tweens.add({
      targets: footer,
      alpha: { from: 0.3, to: 0.8 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: 400,
    });

    // Fade in
    this.scene.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 400,
      ease: 'Quad.easeOut',
    });
    for (const el of elements) {
      if (el === overlay) continue;
      this.scene.tweens.add({
        targets: el,
        alpha: el === bg ? 0.95 : 1,
        duration: 500,
        delay: 250,
        ease: 'Quad.easeOut',
      });
    }

    // Dismiss
    const dismiss = () => {
      this.scene.input.keyboard.off('keydown-ENTER', dismiss);
      this.scene.input.keyboard.off('keydown-SPACE', dismiss);

      try { this.scene.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

      // Fade out
      for (const el of elements) {
        this.scene.tweens.add({
          targets: el,
          alpha: 0,
          duration: 400,
          ease: 'Quad.easeIn',
          onComplete: () => {
            if (el && el.destroy) el.destroy();
          },
        });
      }

      // Phase 11: THE END
      this.scene.time.delayedCall(600, () => {
        this._phase = 'the_end';
        this._showTheEnd();
      });
    };

    this.scene.time.delayedCall(1000, () => {
      this.scene.input.keyboard.on('keydown-ENTER', dismiss);
      this.scene.input.keyboard.on('keydown-SPACE', dismiss);
    });
  }

  // ===============================================================
  // Phase 11: THE END
  // ===============================================================

  _showTheEnd() {
    // Transition sound
    try { this.scene.sound.play(AUDIO_FINAL_TRANSITION, { volume: getSfxVol(0.4) }); } catch (_) {}

    const cam = this.scene.cameras.main;
    const centerX = cam.scrollX + cam.width / 2;
    const centerY = cam.scrollY + cam.height / 2;

    // Player disintegration into smoke particles
    if (this.scene.player) {
      const playerX = this.scene.player.x;
      const playerY = this.scene.player.y;

      // Smoke particles from player
      const smokeEmitter = this.scene.add.particles(playerX, playerY, TEX_SMOKE, {
        lifespan: { min: 1000, max: 3000 },
        speed: { min: 10, max: 40 },
        angle: { min: 220, max: 320 },
        scale: { start: 0.5, end: 0 },
        alpha: { start: 0.6, end: 0 },
        frequency: 40,
        quantity: 3,
        emitting: true,
      });
      smokeEmitter.setDepth(150);

      // Fade out player
      this.scene.tweens.add({
        targets: this.scene.player,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 2000,
        ease: 'Quad.easeIn',
        onComplete: () => {
          smokeEmitter.stop();
        },
      });
    }

    // Darken the entire play area to full black over 2 seconds.
    // All final text will appear only after this blackout completes.
    // World-space oversized rectangle at the camera center.
    const darkScreen = this.scene.add.rectangle(
      cam.scrollX + cam.width / 2,
      cam.scrollY + cam.height / 2,
      cam.width * 4, cam.height * 4,
      0x000000, 0,
    );
    darkScreen.setDepth(500);

    this.scene.tweens.add({
      targets: darkScreen,
      alpha: 1,
      duration: 2000,
      ease: 'Linear',
      onComplete: () => {
        darkScreen.setAlpha(1);
      },
    });

    // After screen is fully black (2s + generous margin), show THE END text.
    // Create a SECOND black rectangle with scrollFactor(0) to act as the
    // permanent background for the text — this one starts at full alpha.
    this.scene.time.delayedCall(2500, () => {
      // Permanent black background for THE END (camera-fixed, covers everything)
      const textBg = this.scene.add.rectangle(
        GAME_WIDTH / 2, GAME_HEIGHT / 2,
        GAME_WIDTH * 3, GAME_HEIGHT * 3,
        0x000000, 1,
      );
      textBg.setDepth(550).setScrollFactor(0);

      // "THE END" appears first on the fully black screen
      const theEndText = this.scene.add.text(
        GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40,
        'THE END', {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '36px',
          fontStyle: 'bold',
          color: '#ffffff',
        },
      ).setOrigin(0.5).setDepth(600).setScrollFactor(0).setAlpha(0);

      const thanksText = this.scene.add.text(
        GAME_WIDTH / 2, GAME_HEIGHT / 2 + 20,
        'Thanks for playing', {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '16px',
          color: '#cccccc',
        },
      ).setOrigin(0.5).setDepth(600).setScrollFactor(0).setAlpha(0);

      const quoteText = this.scene.add.text(
        GAME_WIDTH / 2, GAME_HEIGHT / 2 + 60,
        'Life is about giving; it is something\nbeautiful, so live fully.', {
          fontFamily: '"Courier New", Courier, monospace',
          fontSize: '12px',
          color: '#aaaaaa',
          align: 'center',
          lineSpacing: 4,
        },
      ).setOrigin(0.5).setDepth(600).setScrollFactor(0).setAlpha(0);

      // Fade in "THE END" immediately
      this.scene.tweens.add({
        targets: theEndText,
        alpha: 1,
        duration: 1500,
        ease: 'Quad.easeOut',
      });
      // "Thanks for playing" appears after THE END is visible
      this.scene.tweens.add({
        targets: thanksText,
        alpha: 1,
        duration: 1500,
        delay: 2000,
        ease: 'Quad.easeOut',
      });
      // Quote appears last
      this.scene.tweens.add({
        targets: quoteText,
        alpha: 1,
        duration: 1500,
        delay: 3500,
        ease: 'Quad.easeOut',
      });

      // Delete save and emit completion event
      deleteSave();
      this.eventBus.emit(Events.FINAL_GAME_COMPLETE);

      // Listen for any key press to return to title (after all text is visible)
      this.scene.time.delayedCall(5500, () => {
        const returnToTitle = () => {
          this.scene.input.keyboard.off('keydown', returnToTitle);

          try { this.scene.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.4) }); } catch (_) {}

          // Stop HUD scene
          this.scene.scene.stop(SCENE_HUD);

          // Clean up (stops final music) and go to title
          this.cleanup();

          this.scene.scene.start(SCENE_TITLE);
        };

        this.scene.input.keyboard.on('keydown', returnToTitle);
      });
    });
  }

  // ===============================================================
  // Pause / Resume (for pause menu during battle)
  // ===============================================================

  /**
   * Pause battle-related timers (sphere scheduling, altar/exit proximity checks).
   * Called by GameScene._pauseGameSystems() when the player opens the pause menu.
   */
  pause() {
    if (!this._active) return;

    // Pause sphere scheduling timer
    if (this._sphereTimer && this._sphereTimer.paused !== undefined) {
      this._sphereTimer.paused = true;
    }

    // Pause exit overlap check (walking_to_exit phase)
    if (this._exitCheckEvent && this._exitCheckEvent.paused !== undefined) {
      this._exitCheckEvent.paused = true;
    }

    // Pause altar proximity check (battle phase)
    if (this._altarCheckEvent && this._altarCheckEvent.paused !== undefined) {
      this._altarCheckEvent.paused = true;
    }

    // Pause player proximity check (destruction phase)
    if (this._proximityCheckEvent && this._proximityCheckEvent.paused !== undefined) {
      this._proximityCheckEvent.paused = true;
    }

    // Freeze all active dark spheres
    if (this._sphereGroup) {
      this._sphereGroup.getChildren().forEach((sphere) => {
        if (sphere.body) {
          sphere.setData('_pausedVx', sphere.body.velocity.x);
          sphere.setData('_pausedVy', sphere.body.velocity.y);
          sphere.body.setVelocity(0, 0);
        }
      });
    }
  }

  /**
   * Resume battle-related timers after a pause.
   * Called by GameScene._resumeGameSystems().
   */
  resume() {
    if (!this._active) return;

    // Resume sphere scheduling timer
    if (this._sphereTimer && this._sphereTimer.paused !== undefined) {
      this._sphereTimer.paused = false;
    }

    // Resume exit overlap check
    if (this._exitCheckEvent && this._exitCheckEvent.paused !== undefined) {
      this._exitCheckEvent.paused = false;
    }

    // Resume altar proximity check
    if (this._altarCheckEvent && this._altarCheckEvent.paused !== undefined) {
      this._altarCheckEvent.paused = false;
    }

    // Resume player proximity check
    if (this._proximityCheckEvent && this._proximityCheckEvent.paused !== undefined) {
      this._proximityCheckEvent.paused = false;
    }

    // Restore dark sphere velocities
    if (this._sphereGroup) {
      this._sphereGroup.getChildren().forEach((sphere) => {
        if (sphere.body) {
          const vx = sphere.getData('_pausedVx') || 0;
          const vy = sphere.getData('_pausedVy') || 0;
          sphere.body.setVelocity(vx, vy);
        }
      });
    }
  }

  // ===============================================================
  // Cleanup
  // ===============================================================

  /**
   * Clean up all resources used by the final battle system.
   */
  cleanup() {
    this._active = false;
    this._phase = 'idle';

    // Stop all music
    if (this._celestialHum) {
      try { this._celestialHum.stop(); this._celestialHum.destroy(); } catch (_) {}
      this._celestialHum = null;
    }
    if (this._arenaMusic) {
      try { this._arenaMusic.stop(); this._arenaMusic.destroy(); } catch (_) {}
      this._arenaMusic = null;
    }
    if (this._vorathAmbient) {
      try { this._vorathAmbient.stop(); this._vorathAmbient.destroy(); } catch (_) {}
      this._vorathAmbient = null;
    }
    if (this._threiaMusic) {
      try { this._threiaMusic.stop(); this._threiaMusic.destroy(); } catch (_) {}
      this._threiaMusic = null;
    }
    if (this._finalMusic) {
      try { this._finalMusic.stop(); this._finalMusic.destroy(); } catch (_) {}
      this._finalMusic = null;
    }
    if (this._beamSounds) {
      for (const snd of this._beamSounds) {
        try { snd.stop(); snd.destroy(); } catch (_) {}
      }
      this._beamSounds = [];
    }

    // Stop timers
    for (const timer of this._allTimers) {
      if (timer && timer.remove) {
        try { timer.remove(); } catch (_) {}
      }
    }
    this._allTimers = [];

    if (this._exitCheckEvent) {
      try { this._exitCheckEvent.remove(); } catch (_) {}
      this._exitCheckEvent = null;
    }
    if (this._altarCheckEvent) {
      try { this._altarCheckEvent.remove(); } catch (_) {}
      this._altarCheckEvent = null;
    }
    if (this._proximityCheckEvent) {
      try { this._proximityCheckEvent.remove(); } catch (_) {}
      this._proximityCheckEvent = null;
    }

    // Stop sphere timer
    if (this._sphereTimer) {
      try { this._sphereTimer.remove(); } catch (_) {}
      this._sphereTimer = null;
    }

    // Destroy sphere group
    if (this._sphereGroup) {
      try { this._sphereGroup.clear(true, true); } catch (_) {}
      this._sphereGroup = null;
    }

    // Destroy wall group
    if (this._arenaWallGroup) {
      try { this._arenaWallGroup.clear(true, true); } catch (_) {}
      this._arenaWallGroup = null;
    }

    // Destroy all tracked elements
    for (const el of this._allElements) {
      if (el && el.destroy) {
        try { el.destroy(); } catch (_) {}
      }
    }
    this._allElements = [];

    // Clear references
    this._celestialExitSprite = null;
    this._vorathSprite = null;
    this._altars = [];
    this._arenaGrid = null;
    this._arenaFogGraphics = null;
    this._protectedWallCells = null;
  }
}
